/* eslint-disable @typescript-eslint/no-use-before-define */
import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import {
  PhotoIcon,
  XMarkIcon,
  RocketLaunchIcon,
  ArrowUpTrayIcon,
  ArrowsPointingOutIcon,
  PaintBrushIcon,
  PencilSquareIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  TagIcon,
} from "@heroicons/react/24/outline"
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core"
import { toast } from "react-toastify"
import { useSearchParams } from "react-router-dom"
import { CallToAction, type AdSet } from "~/ui-rtk/api/types/paid-media.types"
import {
  useBulkProductTagsQuery,
  useGetVariantsBulkMutation,
  useLazyAssetIdsByUrlQuery,
  useLazyGetAssetCreativeQuery,
  useLazyGetResolvedCardQuery,
  useResolveAssetsMutation,
} from "~/ui-rtk/api/assetsApi"
import {
  assetToScrapedImage,
  ImageCategory,
  isAvifImage,
  isEnhancedAsset,
  isGenAiChild,
  type PlacementAssets,
  type ScrapedImage,
} from "./types"
import { useGetAdQuery, useSearchAdSetsQuery } from "~/ui-rtk/api/paidMediaApi"
import { mapMediaChannelToSourceType } from "~/ui-rtk/components/atoms/MarathonImage/helpers"
import { useAppDispatch, useAppSelector } from "~/ui-rtk/app/hooks"
import { adForgeActions } from "~/ui-rtk/app/slices/ad-forge.slice"
import {
  selectAdForgeSelectedAssets,
  selectAdForgePickedAssets,
  selectAdForgePrefillCreative,
  selectAdForgeSelectedAdSetIds,
  selectAdForgeSelectedAdSetsData,
  selectAdForgeSourceAd,
  selectAdForgePendingAiAction,
  selectAdForgeProductContext,
} from "~/ui-rtk/app/selectors/ad-forge.selector"
import type { Query as CubeQuery } from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useAssetUpload from "./components/useAssetUpload"
import CreativeForecastPanel from "./components/CreativeForecastPanel"
import { useAnalyzeAssetForForecastMutation } from "~/ui-rtk/api/commentModerationApi"
import ProductMultiSelect, {
  type SelectedFeedProduct,
} from "./components/ProductMultiSelect"
import AiActionDialog, {
  ENHANCE_PROMPT,
  type AiAction,
} from "./components/AiActionDialog"
import ApplyLayoutDialog from "./components/overlay/ApplyLayoutDialog"
import CardOverlayPreview from "./components/overlay/CardOverlayPreview"
import { downloadBlob } from "./components/overlay/export-overlay"
import {
  dimsForRatioLabel,
  measureMediaDims,
  useOverlayBaker,
} from "./components/overlay/overlay-baking"
import PlacementOverlayEditorModal, {
  type PlacementOverlayTarget,
  type PlacementSlotKey,
} from "./components/overlay/PlacementOverlayEditorModal"
import PlacementPreviewModal, {
  type PlacementPreviewTarget,
} from "./components/overlay/PlacementPreviewModal"
import { rescaleDoc } from "./components/overlay/BrandOverlayPanel"
import {
  useAdForgeCreateCreativeMutation,
  useAdForgeGenerateAdCopyMutation,
  useAdForgeGetCreativeQuery,
  useAdForgeGetDefaultsQuery,
  useAdForgeUpdateCreativeMutation,
  type AdCreativeMediaAsset,
} from "~/ui-rtk/api/adForgeApi"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import { useStableNavigate } from "~/ui-rtk/utils/StableNavigateContext"
import {
  isProfileVisitAdSet as isProfileVisit,
  normalizeUrl,
} from "~/ui-rtk/utils/ad-set-utils"
import ScrapeFromUrlDialog from "./components/ScrapeFromUrlDialog"
import AdNameBuilder from "./components/AdNameBuilder"
import AdNameTagSummary from "./components/AdNameTagSummary"
import { useRequiredAdTags } from "./components/useRequiredAdTags"
import { useAssetTagSuggestions } from "./components/useAssetTagSuggestions"
import UnsavedChangesDialog from "./components/UnsavedChangesDialog"
import VideoThumbnailPickerModal from "./components/VideoThumbnailPickerModal"
import { useUnsavedChangesPrompt } from "~/ui-rtk/hooks/unsaved-changes-prompt"
import {
  useExtendPlacements,
  buildExtendPrompt,
  slotForRatio,
  SLOTS as PLACEMENT_SLOTS,
} from "./components/useExtendPlacements"
import {
  useTriggerImageGenMutation,
  useGetJobQuery,
  ACTIVE_JOB_STATUSES,
  type ImageGenCardInput,
} from "~/ui-rtk/api/jobsApi"
import SelectedAssetsDrawer, {
  DRAWER_DROPPABLE_ID,
  DRAWER_ITEM_PREFIX,
} from "./components/SelectedAssetsDrawer"
import AdCardSlots, {
  SLOT_DROPPABLE_PREFIX,
  SLOT_ITEM_PREFIX,
  CAROUSEL_SLOT_COUNT,
} from "./components/AdCardSlots"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { AnimatePresence, motion, type Variants } from "motion/react"

const CTA_OPTIONS = Object.entries(CallToAction).map(([key, value]) => ({
  value,
  label: key
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase()),
}))

const DEFAULT_TRACKING: Array<{ key: string; value: string }> = []

// Serialize the tracking rows into a single "utm_source=x&utm_medium=y"
// string for the paste-the-whole-thing input, and parse it back. The
// `__custom__` sentinel row (a picker with no key yet) is skipped.
const trackingToString = (
  params: Array<{ key: string; value: string }>,
): string =>
  params
    .filter(p => p.key && p.key !== "__custom__")
    .map(p => `${p.key}=${p.value}`)
    .join("&")

const parseTrackingString = (
  input: string,
): Array<{ key: string; value: string }> => {
  let raw = input.trim()
  // Accept a full URL paste — keep only its query string.
  const qIndex = raw.indexOf("?")
  if (raw.includes("://") && qIndex !== -1) raw = raw.slice(qIndex + 1)
  if (raw.startsWith("?")) raw = raw.slice(1)
  // Decode percent-escapes but leave Meta template values like
  // {{campaign.name}} (and malformed escapes) untouched.
  const decode = (s: string) => {
    try {
      return decodeURIComponent(s)
    } catch {
      return s
    }
  }
  return raw
    .split("&")
    .filter(Boolean)
    .map(pair => {
      const eq = pair.indexOf("=")
      if (eq === -1) return { key: decode(pair), value: "" }
      return {
        key: decode(pair.slice(0, eq)),
        value: decode(pair.slice(eq + 1)),
      }
    })
}

// Drop-target id prefix for the right-hand placement tiles. The remainder
// of the id is `${cardId}:${slot}` (split on the LAST `:` so cardIds that
// contain colons still parse correctly).
const PLACEMENT_DROPPABLE_PREFIX = "placement:"

// Animation variants for the right-hand placements showcase. The list
// container staggers its children so the four tiles cascade in instead of
// all popping at once. Tile motion is a short fade + slide-up.
const placementsListVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05, delayChildren: 0.05 },
  },
  exit: {
    opacity: 0,
    transition: { staggerChildren: 0.03, staggerDirection: -1 },
  },
}
const placementTileVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.25, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: { duration: 0.15, ease: "easeIn" },
  },
}

// Drop-target wrapper for a single placement tile. Uses `useDroppable` so
// dnd-kit can route drawer-pool drags onto a specific card+slot.
function PlacementDroppable({
  cardId,
  slot,
  children,
  className,
}: {
  cardId: string
  slot: "landscape" | "portrait" | "stories" | "square"
  children: (args: { isOver: boolean }) => React.ReactNode
  className?: string
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `${PLACEMENT_DROPPABLE_PREFIX}${cardId}:${slot}`,
    data: { kind: "placement", cardId, slot },
  })
  return (
    <div ref={setNodeRef} className={className}>
      {children({ isOver })}
    </div>
  )
}

// The placement slots persisted on a creative. Drives both serialize and
// deserialize so the editor ↔ DTO round-trip stays symmetric. `landscape`
// (1.91:1) is no longer offered in the UI — FB Feed takes the 4:5 portrait
// image now — but it stays here so a draft saved before that change keeps its
// variant through an edit/save round-trip.
const PLACEMENT_SLOT_KEYS = [
  "landscape",
  "portrait",
  "stories",
  "square",
] as const

// Short, user-facing name per slot. Used by the drawer to explain why an asset
// that isn't in any card slot is still marked as in-use.
const PLACEMENT_SLOT_LABELS: Record<
  (typeof PLACEMENT_SLOT_KEYS)[number],
  string
> = {
  landscape: "Feed 1.91:1",
  portrait: "Feed",
  stories: "Stories",
  square: "Square",
}

// Inflate a saved media asset (DTO shape) back into the editor's ScrapedImage
// so a re-opened draft renders identically to a freshly-picked asset.
function mediaAssetToScrapedImage(m: AdCreativeMediaAsset): ScrapedImage {
  return {
    id: m.assetId || m.url,
    url: m.url,
    thumbnailUrl: m.thumbnailUrl ?? m.url,
    type: m.type === "video" ? "video" : "image",
    category: ImageCategory.OTHER,
    color: "Default",
    selected: true,
    ...(m.assetId && { assetId: m.assetId }),
    ...(m.width && { width: m.width }),
    ...(m.height && { height: m.height }),
    sourceUrl: "AdForge draft",
  } as ScrapedImage
}

// Flatten an editor ScrapedImage down to the persisted media-asset shape,
// dropping falsy optionals so the DTO stays clean.
function scrapedImageToMediaAsset(a: ScrapedImage): AdCreativeMediaAsset {
  return {
    type: a.type === "video" ? "video" : "image",
    url: a.url,
    ...(a.assetId && { assetId: a.assetId }),
    ...(a.thumbnailUrl && { thumbnailUrl: a.thumbnailUrl }),
    ...(a.width && { width: a.width }),
    ...(a.height && { height: a.height }),
  }
}

// Serialize a card's per-placement variants to the persisted record. Returns
// undefined when the card has no placement overrides so the DTO omits it.
function serializePlacements(
  pa: PlacementAssets | undefined,
): Record<string, AdCreativeMediaAsset> | undefined {
  if (!pa) return undefined
  const out: Record<string, AdCreativeMediaAsset> = {}
  for (const slot of PLACEMENT_SLOT_KEYS) {
    const img = pa[slot]
    if (img) out[slot] = scrapedImageToMediaAsset(img)
  }
  return Object.keys(out).length ? out : undefined
}

// Inverse of serializePlacements — rebuild a card's PlacementAssets from the
// persisted record so re-opened drafts show their saved per-placement crops.
function deserializePlacements(
  rec: Record<string, AdCreativeMediaAsset> | null | undefined,
): PlacementAssets {
  const out: PlacementAssets = {}
  if (!rec) return out
  for (const slot of PLACEMENT_SLOT_KEYS) {
    const m = rec[slot]
    if (m) out[slot] = mediaAssetToScrapedImage(m)
  }
  return out
}

export default function AdForgePage() {
  const dispatch = useAppDispatch()
  const { isEnabled: isDemoEnabled } = useDemoMode()
  const selectedAssets = useAppSelector(selectAdForgeSelectedAssets)
  const pickedAssets = useAppSelector(selectAdForgePickedAssets)
  const prefillCreative = useAppSelector(selectAdForgePrefillCreative)
  const [generateAdCopy] = useAdForgeGenerateAdCopyMutation()
  // Read-only mirror of prefillCreative for the AI-copy effect, so merging the
  // generated fields doesn't make the effect depend on (and re-run for) every
  // keystroke the user makes in the copy fields.
  const prefillCreativeRef = useRef(prefillCreative)
  useEffect(() => {
    prefillCreativeRef.current = prefillCreative
  }, [prefillCreative])
  const selectedAdSetIds = useAppSelector(selectAdForgeSelectedAdSetIds)
  const selectedAdSetsData = useAppSelector(selectAdForgeSelectedAdSetsData)
  const sourceAd = useAppSelector(selectAdForgeSourceAd)
  const pendingAiAction = useAppSelector(selectAdForgePendingAiAction)
  const productContext = useAppSelector(selectAdForgeProductContext)

  const [draggingFromPool, setDraggingFromPool] = useState(false)

  // Builder state
  const [template, setTemplate] = useState<"single" | "carousel">("single")
  // For carousel template: which card's placements the right-hand showcase
  // is displaying. Clamped to the actual asset list in an effect below so it
  // never points past the end after a card is removed.
  const [selectedCarouselIndex, setSelectedCarouselIndex] = useState(0)
  const [headline, setHeadline] = useState("")
  const [primaryText, setPrimaryText] = useState("")
  const [description, setDescription] = useState("")
  const [destinationUrl, setDestinationUrl] = useState("")
  // Feed products this creative is marked for — on save the backend syncs
  // MANUAL AssetProductTags on every media asset to exactly this selection.
  const [feedProducts, setFeedProducts] = useState<SelectedFeedProduct[]>([])
  // Editor-side fallback when neither the source ad nor the company's
  // `defaultCta` provides a value. Kept in sync with the AdForge defaults
  // section in Settings → Ad Blaster.
  const [callToAction, setCallToAction] = useState("LEARN_MORE")
  const [tracking, setTracking] = useState(DEFAULT_TRACKING)
  // While the single-string UTM input is focused it owns the text (so
  // half-typed pairs like "utm_so" aren't reformatted mid-keystroke);
  // when null the input just mirrors `tracking`.
  const [utmStringDraft, setUtmStringDraft] = useState<string | null>(null)
  const [adName, setAdName] = useState("")
  // Structured tags behind the ad name (tag-category slug → TagOption ids),
  // captured by the "Build from tags" composer. Saved on the draft and applied
  // as TagAssignments on every ad launched from it, so ads are tagged at
  // creation instead of being back-filled from their name later.
  const [adTags, setAdTags] = useState<Record<string, string[]>>({})
  // Tagging is mandatory here: `tagRequirement` is non-null while the selection
  // doesn't satisfy the company's rule (every category the ad-name template
  // names, or — with no template — at least one tag). It gates Save as draft,
  // which is the only way a creative reaches AdBlaster, so nothing untagged
  // ever gets as far as a blast.
  const { validate: validateAdTags, labelForSlug } = useRequiredAdTags()
  const tagRequirement = validateAdTags(adTags)
  // Per-asset placement variants — keyed by the asset's id. Used for both
  // single ads (one entry) and carousel ads (one entry per card).
  const [placementAssetsByAsset, setPlacementAssetsByAsset] = useState<
    Record<string, PlacementAssets>
  >({})
  // Ad-set picker state is gone — AdBlaster owns target selection now. The
  // picker modal, DestinationsBar, and in-page source-blast were stripped;
  // the `selectedAdSetIds` Redux slot is preserved (other readers may still
  // reference it) but is no longer written from AdForge.
  const [adSetSearch] = useState("")
  // Per-placement upload targeting. When set, completing a file pick will
  // route the result into `placementAssetsByAsset[cardId][slot]` instead of
  // the main selection. (Library picks are now done by dragging items from
  // the drawer onto a placement tile — see `handleDragEnd`.)
  type SlotKey = "landscape" | "portrait" | "stories" | "square"
  const [uploadForPlacement, setUploadForPlacement] = useState<{
    cardId: string
    slot: SlotKey
  } | null>(null)
  // Inline "custom prompt" editor for the per-placement AI extend button.
  // Scoped to a single (cardId, slot) so opening the editor on one tile
  // doesn't bleed onto the others. `draft` is the current textarea value.
  const [extendPromptEditor, setExtendPromptEditor] = useState<{
    cardId: string
    slot: SlotKey
    draft: string
  } | null>(null)
  const placementUploadRef = useRef<HTMLInputElement>(null)
  const {
    extendAll: extendAllPlacements,
    isExtending: isExtendingPlacements,
    progress: extendProgress,
  } = useExtendPlacements()
  // Source asset for the always-visible Create with AI panel (image →
  // video, slideshow). Set by the Sparkles button on a filled card slot or
  // by dragging a drawer asset onto the panel's source drop zone; null just
  // means no source picked yet — the panel itself stays on screen.
  const [aiActionAsset, setAiActionAsset] = useState<ScrapedImage | null>(null)
  // Deep-link request handed to AiActionDialog. Set once, from the Mega Feed's
  // "Create ad video" button, so the user lands straight in Image → Video with
  // the product's hero image already the source.
  const [aiActionRequest, setAiActionRequest] = useState<{
    action: AiAction
    token: number
  } | null>(null)

  // AI ad copy for feed-seeded sessions.
  //
  // The Mega Feed seeds primary text with the product's own description, which
  // is catalogue prose — accurate but not ad copy. Once a product is attached,
  // hand its facts to /ad-forge/generate-copy and replace the seed with a
  // written variant. Guarded per product id so it runs once and never
  // re-clobbers what the user has since edited.
  // A feed-seeded session pre-selects its product in the Feed products field
  // (the user can add more or remove it).
  useEffect(() => {
    const productId = productContext?.productId
    if (!productId) return
    setFeedProducts(prev =>
      prev.some(p => p.productRefId === productId)
        ? prev
        : [
            ...prev,
            {
              productRefId: productId,
              title: productContext?.name ?? productId,
            },
          ],
    )
  }, [productContext])

  const copyGeneratedForRef = useRef<string | null>(null)
  useEffect(() => {
    const productId = productContext?.productId
    if (!productId || copyGeneratedForRef.current === productId) return
    copyGeneratedForRef.current = productId

    const sourceText = [
      productContext.name,
      productContext.headerDescription,
      productContext.specs,
      productContext.productType ?? productContext.googleProductCategory,
    ].filter((v): v is string => Boolean(v))

    void (async () => {
      try {
        const copy = await generateAdCopy({
          sourceText,
          sourceReviews: [],
          campaignType: "product",
          tags: (productContext.tags ?? "")
            .split(",")
            .map(t => t.trim())
            .filter(Boolean),
          productDescription:
            productContext.description ??
            productContext.headerDescription ??
            "",
        }).unwrap()

        // Take the first variant of each field; the user can regenerate or edit
        // in the form. Only overwrite what the model actually returned.
        dispatch(
          adForgeActions.setPrefillCreative({
            ...prefillCreativeRef.current,
            ...(copy.meta.primary?.[0] && {
              primaryText: copy.meta.primary[0],
            }),
            ...(copy.meta.headline?.[0] && { headline: copy.meta.headline[0] }),
            ...(copy.meta.description?.[0] && {
              description: copy.meta.description[0],
            }),
          }),
        )
      } catch {
        // Non-fatal: the feed-derived copy stays in place.
      }
    })()
  }, [productContext, dispatch, generateAdCopy])

  // Consume the one-click intent from the Mega Feed exactly once: pick the
  // first pooled asset as the source and deep-link into the flow. Cleared from
  // Redux immediately so navigating back to AdForge later doesn't re-open it.
  useEffect(() => {
    if (!pendingAiAction) return
    const hero = selectedAssets[0] ?? pickedAssets[0]
    if (!hero) return
    setAiActionAsset(hero)
    setAiActionRequest({ action: pendingAiAction, token: Date.now() })
    dispatch(adForgeActions.setPendingAiAction(undefined))
  }, [pendingAiAction, selectedAssets, pickedAssets, dispatch])
  // Card whose media the Apply-layout dialog is baking a saved overlay onto.
  const [applyLayoutAsset, setApplyLayoutAsset] = useState<ScrapedImage | null>(
    null,
  )
  // Video card whose thumbnail is being picked from the video's own frames.
  const [thumbnailPickerAsset, setThumbnailPickerAsset] =
    useState<ScrapedImage | null>(null)
  // The picker persists via POST /assets/:id/thumbnail, so it needs a library
  // id — but plenty of staged videos arrive without one (old drafts, scrapes,
  // feed seeds). Resolve it by URL on demand instead of hiding the button.
  const [lookupAssetIdsByUrl] = useLazyAssetIdsByUrlQuery()
  const handlePickThumbnail = useCallback(
    async (asset: ScrapedImage) => {
      if (asset.assetId) {
        setThumbnailPickerAsset(asset)
        return
      }
      try {
        const { ids } = await lookupAssetIdsByUrl({
          urls: [asset.url],
        }).unwrap()
        const assetId = ids[asset.url]
        if (!assetId) {
          toast.error(
            "This video isn't in the asset library yet — upload it (or re-add it from the Latest tab) to pick a thumbnail.",
          )
          return
        }
        // Stamp the resolved id back onto the card/pool copy so the next
        // open (and the draft save) skips the lookup.
        dispatch(
          adForgeActions.updateSelectedAssetById({
            id: asset.id,
            updates: { assetId },
          }),
        )
        setThumbnailPickerAsset({ ...asset, assetId })
      } catch {
        toast.error("Couldn't look up this video in the asset library.")
      }
    },
    [lookupAssetIdsByUrl, dispatch],
  )
  // Placement tile being arranged in the per-placement layout editor.
  const [placementOverlayTarget, setPlacementOverlayTarget] =
    useState<PlacementOverlayTarget | null>(null)
  // Placement opened in the full-page preview.
  const [placementPreviewTarget, setPlacementPreviewTarget] =
    useState<PlacementPreviewTarget | null>(null)
  // True while an AI flow (not just the button row) is open. The asset
  // section is hidden during a flow so the flow UI takes its place.
  const [isAiFlowOpen, setIsAiFlowOpen] = useState(false)

  // Tracks which card ids the active background image-gen job covers so
  // AdCardSlots can render a loading overlay over each affected card. Cleared
  // in one shot when the job reaches a terminal status (see the poll effect).
  const [enhancingAssetIds, setEnhancingAssetIds] = useState<
    ReadonlySet<string>
  >(() => new Set())

  // Parent asset ids whose saved AI variants are currently being fetched by the
  // bulk prefill lookup (getVariantsBulk). Keyed by library `assetId` (the
  // request's `parentAssetIds`). Drives a loading overlay on the image cards
  // and their placement tiles so the user sees that placements are still
  // resolving rather than an empty/"no asset" state.
  const [prefillingAssetIds, setPrefillingAssetIds] = useState<
    ReadonlySet<string>
  >(() => new Set())

  // Per-placement extend (custom-prompt) in flight. Keyed by `${cardId}:${slot}`
  // so the spinner sits on the exact tile being regenerated rather than the
  // shared global `isExtendingPlacements` flag (which would spin every tile).
  const [extendingPlacementKeys, setExtendingPlacementKeys] = useState<
    ReadonlySet<string>
  >(() => new Set())

  // Source-mode: clone an existing Meta/TikTok ad OR promote an organic post.
  // Reuses the same builder form — assets/template/placement are skipped
  // because the backend clones them from the source. The actual launch now
  // happens from AdBlaster's BlastTargetPickerModal; this page is just the
  // creative editor for the source.
  const isSourcePost = sourceAd?.type === "post"
  const sourceRefId = isSourcePost ? sourceAd?.postId : sourceAd?.adId
  // In source mode (organic post or existing ad blast), hide the creative
  // override fields by default — the user's intent is "use this post / ad
  // as-is, no custom things." They can click "Edit creative" to expand
  // and override fields if needed. addIfEdited downstream only sends
  // overrides for fields the user actually changed, so leaving this
  // collapsed = backend gets just the source id.
  const [showCreativeFields, setShowCreativeFields] = useState(false)
  // Previously the in-page source-blast catch wrote `requiredFields` from a
  // structured Meta error envelope (subcode 2061015 "website URL required").
  // That blast lives in AdBlaster's BlastTargetPickerModal now, so the array
  // is permanently empty here. Existing readers (the banner, retry hint,
  // fields-unlock check) still work — they always take the "no required
  // fields" branch.
  const requiredFields: string[] = []
  // Tracks the adId we've already prefilled the form from. Once set, we stop
  // re-running `useGetAdQuery` for that ad — otherwise `blastAd`'s "Ad" tag
  // invalidation would refetch the source creative after every blast attempt.
  const [prefilledFromAdId, setPrefilledFromAdId] = useState<string | null>(
    null,
  )
  // Set true whenever the form is populated programmatically (edit-mode
  // hydrate, source/nav prefill, company defaults) so the unsaved-changes
  // baseline re-snaps to that state — only genuine user edits count as dirty.
  const pendingRebaselineRef = useRef(false)
  const { data: sourceAdData, isFetching: isFetchingSource } = useGetAdQuery(
    { platform: sourceAd?.platform ?? "meta", adId: sourceAd?.adId ?? "" },
    {
      skip:
        !sourceAd?.adId ||
        // Posts don't have a separate ad creative to fetch — caption-driven prefill is handled below.
        isSourcePost ||
        // Already prefilled from this source — don't refetch on cache invalidation.
        prefilledFromAdId === sourceAd.adId,
    },
  )

  // Hand the source ad's creative to the existing prefill effect so the
  // builder's form fields hydrate without us touching them directly.
  useEffect(() => {
    if (!sourceAd?.adId) return
    if (prefilledFromAdId === sourceAd.adId) return
    if (!sourceAdData?.creative) return
    const c = sourceAdData.creative
    dispatch(
      adForgeActions.setPrefillCreative({
        primaryText: c.primaryText,
        headline: c.headline,
        description: c.description,
        destinationUrl: c.destinationUrl,
        callToAction: c.callToAction,
        adName: sourceAdData.name,
      }),
    )
    setPrefilledFromAdId(sourceAd.adId)
  }, [sourceAd?.adId, sourceAdData, dispatch, prefilledFromAdId])

  // Post mode has no ad-creative to fetch — seed primary text from the
  // organic post's caption and the ad name from the post's display name.
  // `destinationUrl` is intentionally left blank so the user picks where the
  // cloned ad sends traffic.
  useEffect(() => {
    if (!isSourcePost || !sourceRefId) return
    if (prefilledFromAdId === sourceRefId) return
    dispatch(
      adForgeActions.setPrefillCreative({
        primaryText: sourceAd?.caption,
        adName: sourceAd?.name,
      }),
    )
    setPrefilledFromAdId(sourceRefId)
  }, [
    isSourcePost,
    sourceRefId,
    sourceAd?.caption,
    sourceAd?.name,
    dispatch,
    prefilledFromAdId,
  ])

  // Company-level defaults: seed the destination URL on fresh drafts when
  // there's no source ad to inherit from. Guarded so we never clobber a value
  // the user (or a source prefill) already wrote — only fires while the field
  // is empty.
  const { data: adForgeDefaults } = useAdForgeGetDefaultsQuery(undefined, {
    skip: !!sourceAd,
  })
  const [defaultUrlApplied, setDefaultUrlApplied] = useState(false)
  useEffect(() => {
    if (sourceAd) return
    if (defaultUrlApplied) return
    const fallback = adForgeDefaults?.destinationUrl
    if (!fallback) return
    if (destinationUrl) return
    setDestinationUrl(fallback)
    setDefaultUrlApplied(true)
    pendingRebaselineRef.current = true
  }, [sourceAd, adForgeDefaults, destinationUrl, defaultUrlApplied])

  // Same pattern for the company default CTA. Only applies if the picker is
  // still on the editor fallback ("LEARN_MORE") so a user-picked value or a
  // source-prefill CTA is never overwritten. One-shot via `defaultCtaApplied`.
  const [defaultCtaApplied, setDefaultCtaApplied] = useState(false)
  useEffect(() => {
    if (sourceAd) return
    if (defaultCtaApplied) return
    const fallback = adForgeDefaults?.defaultCta
    if (!fallback) return
    if (callToAction !== "LEARN_MORE") return
    setCallToAction(fallback)
    setDefaultCtaApplied(true)
    pendingRebaselineRef.current = true
  }, [sourceAd, adForgeDefaults, callToAction, defaultCtaApplied])

  // Resolve the source's image/video via /assets/resolve and seed
  // selectedAssets so the right-side preview renders the actual creative.
  const [resolveAssets, { isLoading: isResolvingAsset }] =
    useResolveAssetsMutation()
  // While we're hydrating from the source, lock the form so the user can't
  // type into a field that's about to be overwritten by the prefill.
  const isPrefillingSource =
    !!sourceAd && (isFetchingSource || isResolvingAsset)
  const sourceAssetResolvedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!sourceRefId) return
    if (sourceAssetResolvedRef.current === sourceRefId) return
    sourceAssetResolvedRef.current = sourceRefId
    // Posts live under their organic source type (INSTAGRAM/TIKTOK/YOUTUBE);
    // ads live under the paid type (FACEBOOK_AD/TIKTOK_AD).
    const resolveSourceType = isSourcePost
      ? mapMediaChannelToSourceType(sourceAd?.mediaChannel ?? "", true)
      : sourceAd?.platform === "tiktok"
        ? "TIKTOK_AD"
        : "FACEBOOK_AD"
    void (async () => {
      try {
        const res = await resolveAssets({
          assets: [
            {
              sourceType: resolveSourceType,
              externalRefId: sourceRefId,
              ...(sourceAd?.imageUrl && { url: sourceAd.imageUrl }),
            },
          ],
        }).unwrap()
        const cacheKey = `${resolveSourceType}:${sourceRefId}`
        const resolved = res.results[cacheKey]
        if (resolved && resolved.syncStatus === "SYNCED") {
          dispatch(
            adForgeActions.setSelectedAssets([assetToScrapedImage(resolved)]),
          )
        }
      } catch {
        // Fallback handled by source card image; nothing to do.
      }
    })()
  }, [
    sourceRefId,
    isSourcePost,
    sourceAd?.platform,
    sourceAd?.mediaChannel,
    sourceAd?.imageUrl,
    resolveAssets,
    dispatch,
  ])

  const mainUploadInputRef = useRef<HTMLInputElement>(null)
  const [showScrapeDialog, setShowScrapeDialog] = useState(false)
  const [showAdNameBuilder, setShowAdNameBuilder] = useState(false)

  const { uploadFiles, isUploading } = useAssetUpload()
  const [queueAssetAnalysis] = useAnalyzeAssetForForecastMutation()
  /**
   * Every asset that enters the builder through an upload (manual, AI
   * generation, motion video) is analysed and embedded in the background so
   * the audience forecast is ready by the time it is on the ad. Best-effort:
   * the job dedupes on the asset ids, and the forecast panel re-queues if a
   * run never happened.
   */
  const analyzeNewAssets = (assets: Array<{ assetId?: string }>) => {
    const assetIds = assets
      .map(a => a.assetId)
      .filter((id): id is string => !!id)
    if (assetIds.length === 0) return
    queueAssetAnalysis({ assetIds })
      .unwrap()
      .catch(err => console.warn("Could not queue creative analysis", err))
  }

  // Pick the closest Imagen-supported aspect ratio for a source image so
  // the enhanced output preserves orientation instead of squishing into
  // a square default.
  const pickClosestImagenRatio = useCallback(
    (width: number, height: number): string => {
      if (!width || !height) return "1:1"
      const ratio = width / height
      const candidates: { label: string; value: number }[] = [
        { label: "9:16", value: 9 / 16 },
        { label: "3:4", value: 3 / 4 },
        { label: "4:5", value: 4 / 5 },
        { label: "1:1", value: 1 },
        { label: "4:3", value: 4 / 3 },
        { label: "16:9", value: 16 / 9 },
      ]
      let best = candidates[0]
      let bestDiff = Math.abs(ratio - best.value)
      for (const c of candidates.slice(1)) {
        const diff = Math.abs(ratio - c.value)
        if (diff < bestDiff) {
          bestDiff = diff
          best = c
        }
      }
      return best.label
    },
    [],
  )

  // Fetch a parent asset's saved AI children (enhanced mains + placement
  // variants). Used both to pre-fill placement slots and to skip re-enhancing
  // a card that already has a saved enhanced version.
  const [getVariantsBulk] = useGetVariantsBulkMutation()

  // Resolve any picked AI variant back into its original "card" (the source
  // image + its per-placement variants). Used to reassemble a card when the
  // user selects a child (enhanced main or extend crop) instead of the source.
  const [resolveCard] = useLazyGetResolvedCardQuery()

  // Background image-gen job (enhance + extend). Replaces the old synchronous
  // per-card AI loop: we enqueue ONE durable job, poll it, and apply the
  // persisted GEN_AI variants when it finishes. The user can navigate away —
  // results are saved server-side and re-prefilled on return.
  const [triggerImageGen] = useTriggerImageGenMutation()
  // ONE durable job per action (the job itself generates images in parallel
  // batches server-side). We poll that single job and apply its persisted
  // results when it finishes.
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  // `isStarting` flips synchronously on click so the button disables + cards
  // show spinners immediately, before the variant-check / enqueue round-trips.
  const [isStarting, setIsStarting] = useState(false)
  const jobsRunning = isStarting || !!activeJobId
  // Which parents the active job covers (+ whether it enhanced) so the apply
  // step knows what to swap/fill once the job completes.
  const activeJobMetaRef = useRef<{
    parentAssetIds: string[]
    enhance: boolean
  } | null>(null)
  // Always-fresh view of selectedAssets for the apply step — avoids stale
  // closures when the job finishes long after the enqueue render.
  const selectedAssetsRef = useRef(selectedAssets)
  useEffect(() => {
    selectedAssetsRef.current = selectedAssets
  }, [selectedAssets])

  // Build a ScrapedImage for the editor from a saved GEN_AI variant. Mirrors
  // the shape the auto-prefill effect produces so swapped mains / filled
  // placements look identical to library-sourced assets.
  const variantToScrapedImage = useCallback(
    (
      variant: {
        id: string
        url?: string | null
        thumbnailUrl?: string | null
        mediaType?: string
        width?: number | null
        height?: number | null
        metadata?: Record<string, unknown> | null
      },
      sourceLabel: string,
    ): ScrapedImage | null => {
      const url = variant.url || variant.thumbnailUrl
      if (!url) return null
      return {
        id: variant.id,
        url,
        thumbnailUrl: variant.thumbnailUrl ?? url,
        type: variant.mediaType === "VIDEO" ? "video" : "image",
        category: ImageCategory.OTHER,
        color: "Default",
        selected: true,
        assetId: variant.id,
        sourceUrl: sourceLabel,
        width: variant.width ?? undefined,
        height: variant.height ?? undefined,
        // Carry provenance (generatedBy/aspectRatio) so a swapped-in enhanced
        // card is recognized as already-enhanced — prevents re-enhancing it and
        // suppresses the low-res warning on the enhanced output.
        metadata: variant.metadata ?? undefined,
      } as ScrapedImage
    },
    [],
  )

  // Apply a finished image-gen job's persisted results. Re-fetches each
  // parent's variants and either swaps the enhanced main into selectedAssets
  // (enhance path — placements auto-prefill once the card's assetId changes)
  // or fills the placement slots directly (fix-only path — the prefill effect
  // already ran for the original parent and won't refetch).
  const applyJobResults = useCallback(
    async (parentAssetIds: string[], enhance: boolean) => {
      if (parentAssetIds.length === 0) return
      let results: Record<
        string,
        Array<{
          id: string
          url?: string | null
          thumbnailUrl?: string | null
          mediaType?: string
          width?: number | null
          height?: number | null
          metadata?: Record<string, unknown> | null
        }>
      > = {}
      try {
        results = (await getVariantsBulk({ parentAssetIds }).unwrap()).results
      } catch {
        return
      }

      if (enhance) {
        // Swap each parent's card to its newly-enhanced main. Use the live ref
        // so a single dispatch reflects every swap (no stale-closure clobbering).
        let nextSelected = selectedAssetsRef.current.slice()
        let changed = false
        for (const parentAssetId of parentAssetIds) {
          const variants = results[parentAssetId] ?? []
          const enhanced = variants.find(v =>
            String(v.metadata?.generatedBy ?? "").includes("enhance"),
          )
          if (!enhanced) continue
          const scraped = variantToScrapedImage(enhanced, "AI (enhanced)")
          if (!scraped) continue
          nextSelected = nextSelected.map(a =>
            a.assetId === parentAssetId ? scraped : a,
          )
          changed = true
        }
        if (changed) {
          selectedAssetsRef.current = nextSelected
          dispatch(adForgeActions.setSelectedAssets(nextSelected))
        }
        return
      }

      // Fix-only: placement variants hang off the ORIGINAL parent. The prefill
      // effect already registered these parents in `prefilledForRef` and won't
      // refetch, so fill the slots ourselves without clobbering existing ones.
      const cardIdByAssetId = new Map<string, string>()
      for (const a of selectedAssetsRef.current) {
        if (a.assetId) cardIdByAssetId.set(a.assetId, a.id)
      }
      setPlacementAssetsByAsset(prev => {
        let changed = false
        const next = { ...prev }
        for (const parentAssetId of parentAssetIds) {
          const cardId = cardIdByAssetId.get(parentAssetId)
          if (!cardId) continue
          const variants = results[parentAssetId] ?? []
          let updated = { ...(next[cardId] ?? {}) }
          for (const variant of variants) {
            const aspectRatio =
              typeof variant.metadata?.aspectRatio === "string"
                ? variant.metadata.aspectRatio
                : undefined
            const slot = slotForRatio(aspectRatio)
            if (!slot) continue
            if (updated[slot]) continue // never clobber an existing variant
            const scraped = variantToScrapedImage(variant, "AI (extended)")
            if (!scraped) continue
            updated = { ...updated, [slot]: scraped }
            changed = true
          }
          if (changed) next[cardId] = updated
        }
        return changed ? next : prev
      })
    },
    [getVariantsBulk, dispatch, variantToScrapedImage],
  )

  // Enqueue one background image-gen job PER card, all in parallel, then leave.
  // A single up-front bulk variants lookup skips already-done work (existing
  // enhanced main / placement ratio) so we never obviously re-spend. Each
  // card's job applies its persisted results independently as it finishes.
  const runBulkImageJob = useCallback(
    async (cards: ScrapedImage[], opts: { enhance: boolean }) => {
      if (jobsRunning) return
      // AVIF can't go through the image-edit pipeline (stock sharp can't decode
      // it) — exclude it the same way videos are, before anything is enqueued.
      const enhanceable = cards.filter(c => !isAvifImage(c))
      const skippedAvif = cards.length - enhanceable.length
      const withAssetId = enhanceable.filter(c => c.assetId)
      if (withAssetId.length === 0) {
        toast.error(
          skippedAvif > 0
            ? "AVIF images can't be enhanced — re-upload as JPEG or PNG."
            : "Re-upload these from the drawer first.",
        )
        return
      }
      if (skippedAvif > 0) {
        toast(
          `Skipping ${skippedAvif} AVIF image${skippedAvif === 1 ? "" : "s"} — re-upload as JPEG or PNG to enhance.`,
        )
      }

      // Immediate loading state (synchronous) — the button disables and every
      // targeted card shows a spinner the instant the user clicks, before the
      // variant-check / enqueue round-trips resolve.
      setIsStarting(true)
      setEnhancingAssetIds(new Set(withAssetId.map(c => c.id)))

      try {
        // One lookup up front to skip already-generated work. Best-effort —
        // failures just fall through to generating everything.
        let existing: Record<
          string,
          Array<{
            id: string
            url?: string | null
            thumbnailUrl?: string | null
            mediaType?: string
            width?: number | null
            height?: number | null
            metadata?: Record<string, unknown> | null
          }>
        > = {}
        try {
          const { results } = await getVariantsBulk({
            parentAssetIds: withAssetId.map(c => c.assetId as string),
          }).unwrap()
          existing = results
        } catch {
          existing = {}
        }

        const jobCards: ImageGenCardInput[] = []
        const includedCardIds: string[] = []
        const includedParentIds: string[] = []
        for (const card of withAssetId) {
          const parentAssetId = card.assetId as string
          const variants = existing[parentAssetId] ?? []
          // Already-enhanced if the card IS an enhanced output (it was swapped
          // in after a prior enhance — its own assetId is the enhanced child, so
          // it has no enhance-children to find) OR it has a saved enhanced child.
          const hasEnhanced =
            isEnhancedAsset(card) ||
            variants.some(v =>
              String(v.metadata?.generatedBy ?? "").includes("enhance"),
            )
          const presentRatios = new Set(
            variants
              .map(v =>
                typeof v.metadata?.aspectRatio === "string"
                  ? v.metadata.aspectRatio
                  : undefined,
              )
              .filter((r): r is string => !!r),
          )

          // Only actually enhance when asked AND not already enhanced.
          const willEnhance = opts.enhance && !hasEnhanced
          const input: ImageGenCardInput = { parentAssetId }
          if (willEnhance) {
            input.enhanceMain = {
              prompt: ENHANCE_PROMPT,
              aspectRatio: pickClosestImagenRatio(
                card.width ?? 0,
                card.height ?? 0,
              ),
            }
          }
          // When we enhance, placements derive from the NEW enhanced main, so
          // regenerate all four. Otherwise (fix-only, or an already-enhanced
          // card) only generate the ratios that don't already exist.
          const placements = PLACEMENT_SLOTS.filter(
            cfg => willEnhance || !presentRatios.has(cfg.displayRatio),
          ).map(cfg => ({
            slot: cfg.slot,
            aspectRatio: cfg.displayRatio,
            prompt: buildExtendPrompt(cfg.displayRatio),
          }))
          if (placements.length > 0) input.placements = placements

          if (!input.enhanceMain && !input.placements) continue
          jobCards.push(input)
          includedCardIds.push(card.id)
          includedParentIds.push(parentAssetId)
        }

        // A card that already has a saved enhanced child but whose selected
        // entry still points at the ORIGINAL low-res asset gets no job (nothing
        // to generate), so `applyJobResults` never swaps it — leaving the card
        // on the original and the low-res warning stuck on. Swap those in now,
        // synchronously, so the card uses the enhanced asset (and the warning
        // clears) whether or not a job runs.
        if (opts.enhance) {
          let nextSelected = selectedAssetsRef.current.slice()
          let swapped = false
          for (const card of withAssetId) {
            if (isEnhancedAsset(card)) continue
            const parentAssetId = card.assetId as string
            const enhanced = (existing[parentAssetId] ?? []).find(v =>
              String(v.metadata?.generatedBy ?? "").includes("enhance"),
            )
            if (!enhanced) continue
            const scraped = variantToScrapedImage(enhanced, "AI (enhanced)")
            if (!scraped) continue
            nextSelected = nextSelected.map(a =>
              a.assetId === parentAssetId ? scraped : a,
            )
            swapped = true
          }
          if (swapped) {
            selectedAssetsRef.current = nextSelected
            dispatch(adForgeActions.setSelectedAssets(nextSelected))
          }
        }

        if (jobCards.length === 0) {
          setEnhancingAssetIds(new Set())
          toast.info("Everything's already generated.")
          return
        }

        // Narrow the spinner to the cards we'll actually generate (drop any that
        // were already fully done and got skipped above).
        setEnhancingAssetIds(new Set(includedCardIds))

        // ONE job for the whole action — the job generates images in parallel
        // batches server-side. Single job id → a single poll (no request flood).
        const { jobRunId } = await triggerImageGen({ cards: jobCards }).unwrap()
        activeJobMetaRef.current = {
          parentAssetIds: includedParentIds,
          enhance: opts.enhance,
        }
        setActiveJobId(jobRunId)
        toast.success(
          "Working in the background — you can leave this screen; we'll save the results.",
        )
      } catch {
        setEnhancingAssetIds(new Set())
        toast.error("Couldn't start the job. Try again.")
      } finally {
        setIsStarting(false)
      }
    },
    [
      jobsRunning,
      getVariantsBulk,
      pickClosestImagenRatio,
      triggerImageGen,
      dispatch,
      variantToScrapedImage,
    ],
  )

  // Poll the single active job (one request every 4s) and apply its persisted
  // results on completion. Cleared on terminal → poll stops.
  const { data: activeJob } = useGetJobQuery(activeJobId ?? "", {
    skip: !activeJobId,
    pollingInterval: activeJobId ? 4000 : 0,
  })
  useEffect(() => {
    const status = activeJob?.status
    if (!status || ACTIVE_JOB_STATUSES.includes(status)) return
    const meta = activeJobMetaRef.current
    void (async () => {
      if (meta) await applyJobResults(meta.parentAssetIds, meta.enhance)
      activeJobMetaRef.current = null
      setActiveJobId(null)
      setEnhancingAssetIds(new Set())
      const summary = activeJob?.result?.summary
      if (summary && summary.failed === 0 && summary.successful > 0) {
        toast.success(
          `Done — ${summary.successful} image${summary.successful === 1 ? "" : "s"} generated.`,
        )
      } else if (summary && summary.successful > 0) {
        toast.warning(
          `${summary.successful}/${summary.total} generated — ${summary.failed} failed.`,
        )
      } else {
        toast.error("Image generation failed. Try again.")
      }
    })()
    // Fire only on status transition (applyJobResults is stable; result is in
    // the same response that flips status to terminal).
  }, [activeJob?.status])

  // For single template, use only the first asset; for carousel, use all (up to 10)
  const adAssets =
    template === "single"
      ? selectedAssets.slice(0, 1)
      : selectedAssets.slice(0, 10)
  /** The library asset the audience forecast is keyed on: the ad's first card. */
  const forecastAssetId = adAssets[0]?.assetId
  // The asset a saved draft opened with: its picks were already offered when
  // the draft was built, so opening the draft must not re-tag it.
  const draftAssetIdRef = useRef<string | undefined>(undefined)
  // An asset with a saved creative analysis brings its tag picks along: they
  // fill the ad's empty tag categories the moment the asset is on the ad.
  const { refresh: refreshAssetTagSuggestions } = useAssetTagSuggestions({
    assetId: forecastAssetId,
    metadata: adAssets[0]?.metadata,
    current: adTags,
    skipAssetId: draftAssetIdRef.current,
    onApply: (next, filled) => {
      setAdTags(next)
      toast.info(
        `Tags applied from the creative analysis: ${filled.map(f => f.label).join(", ")}`,
      )
    },
  })

  // Asset id → the placement slot labels it fills, so the drawer can mark a
  // per-placement variant as in-use. Without this a re-opened draft's saved
  // placement assets look like any other library image in the list even
  // though they're part of the ad. Both the local card id and the library
  // `assetId` are registered — the drawer's catalog tabs key off the library
  // id, the Selected pool off whatever id the variant was created with.
  const placementLabelsByAssetId = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const variants of Object.values(placementAssetsByAsset)) {
      for (const slot of PLACEMENT_SLOT_KEYS) {
        const variant = variants[slot]
        if (!variant) continue
        const label = PLACEMENT_SLOT_LABELS[slot]
        for (const key of [variant.id, variant.assetId]) {
          if (!key) continue
          const list = map.get(key) ?? []
          if (!list.includes(label)) list.push(label)
          map.set(key, list)
        }
      }
    }
    return map
  }, [placementAssetsByAsset])

  // Prune placement variants for assets that are no longer in the cart so
  // a removed/swapped image doesn't leak its (now-stale) per-placement
  // crops/uploads into the launch payload.
  const adAssetIdsKey = adAssets.map(a => a.id).join("|")
  useEffect(() => {
    setPlacementAssetsByAsset(prev => {
      const validIds = new Set(adAssetIdsKey ? adAssetIdsKey.split("|") : [])
      let changed = false
      const next: Record<string, PlacementAssets> = {}
      for (const [id, value] of Object.entries(prev)) {
        if (validIds.has(id)) next[id] = value
        else changed = true
      }
      return changed ? next : prev
    })
  }, [adAssetIdsKey])

  // Auto-prefill placement slots with existing AI variants. Whenever a new
  // asset (with a library `assetId`) enters the cards, ask the backend for
  // its children (created via the AI extend flow — they carry
  // `metadata.aspectRatio`). Map each child to the matching slot and merge
  // into `placementAssetsByAsset` WITHOUT overwriting anything the user
  // already set or auto-generated this session. Saves duplicate AI calls.
  const prefilledForRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    // Only ask for parents we haven't asked for yet this session.
    const ids = adAssets
      .map(a => a.assetId)
      .filter((id): id is string => !!id && !prefilledForRef.current.has(id))
    if (ids.length === 0) return
    for (const id of ids) prefilledForRef.current.add(id)

    setPrefillingAssetIds(prev => new Set([...prev, ...ids]))
    getVariantsBulk({ parentAssetIds: ids })
      .unwrap()
      .then(({ results }) => {
        setPlacementAssetsByAsset(prev => {
          let changed = false
          const next = { ...prev }
          // Match each parent's child variants back to the local ScrapedImage
          // by `assetId` (parents come in as ScrapedImage; we need a card id).
          const cardIdByAssetId = new Map<string, string>()
          for (const a of adAssets) {
            if (a.assetId) cardIdByAssetId.set(a.assetId, a.id)
          }

          for (const [parentAssetId, variants] of Object.entries(results)) {
            const cardId = cardIdByAssetId.get(parentAssetId)
            if (!cardId) continue
            const existing = next[cardId] ?? {}
            let updated = { ...existing }
            for (const variant of variants) {
              const metaRaw = (
                variant as { metadata?: Record<string, unknown> }
              ).metadata
              const aspectRatio =
                typeof metaRaw?.aspectRatio === "string"
                  ? metaRaw.aspectRatio
                  : undefined
              const slot = slotForRatio(aspectRatio)
              if (!slot) continue
              if (updated[slot]) continue // never clobber an existing variant
              const url = variant.url || variant.thumbnailUrl
              if (!url) continue
              updated = {
                ...updated,
                [slot]: {
                  id: variant.id,
                  url,
                  thumbnailUrl: variant.thumbnailUrl ?? url,
                  type: variant.mediaType === "VIDEO" ? "video" : "image",
                  category: ImageCategory.OTHER,
                  color: "Default",
                  selected: true,
                  assetId: variant.id,
                  sourceUrl: "AI (existing variant)",
                  width: variant.width ?? undefined,
                  height: variant.height ?? undefined,
                },
              }
              changed = true
            }
            if (existing !== updated) next[cardId] = updated
          }
          return changed ? next : prev
        })
      })
      .catch(() => {
        /* swallow — empty variants is the safe default */
      })
      .finally(() => {
        setPrefillingAssetIds(prev => {
          const next = new Set(prev)
          for (const id of ids) next.delete(id)
          return next
        })
      })
    // adAssetIdsKey is enough to detect "the set of cards changed"; we
    // intentionally don't depend on `adAssets` itself (would refire on
    // unrelated re-renders) or `getVariantsBulk` (stable mutation ref).
  }, [adAssetIdsKey])

  // When a picked card is itself an AI-generated child (an enhanced main or an
  // extend/placement crop), resolve it back to its ORIGINAL source image as the
  // card's main and auto-fill the placement slots from the sibling variants —
  // so dropping any single variant reassembles the whole card. The card keeps
  // its local `id` (only the underlying image/assetId swaps), so this doesn't
  // perturb adAssetIdsKey or the prefill/pruning effects.
  //
  // ONLY lineage variants (enhance/extend — the same creative at another
  // size) resolve this way. AI compositions (ad frames, carousel cards,
  // variations, slideshows) are their own artwork that merely share a
  // parent — resolving them collapsed every AI-carousel card into the
  // same hero image.
  const isLineageVariant = (c: ScrapedImage) => {
    const generatedBy = String(c.metadata?.generatedBy ?? "")
    return generatedBy.includes("extend") || generatedBy.includes("enhance")
  }
  const resolvedChildRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const childCards = adAssets.filter(
      c =>
        c.assetId &&
        isGenAiChild(c) &&
        isLineageVariant(c) &&
        !resolvedChildRef.current.has(c.assetId),
    )
    if (childCards.length === 0) return
    for (const c of childCards) {
      resolvedChildRef.current.add(c.assetId as string)
    }

    void (async () => {
      const swaps = new Map<
        string,
        { main: ScrapedImage; placements: PlacementAssets }
      >()
      for (const c of childCards) {
        try {
          const resolved = await resolveCard(c.assetId as string).unwrap()
          if (!resolved.main) continue
          // Keep the card's local id stable; only the image/assetId changes.
          const main: ScrapedImage = {
            ...assetToScrapedImage(resolved.main),
            id: c.id,
          }
          // The resolver already returns the full placement set, so block the
          // generic prefill from also running on the new main.
          if (main.assetId) prefilledForRef.current.add(main.assetId)

          const placements: PlacementAssets = {}
          for (const slot of [
            "landscape",
            "portrait",
            "stories",
            "square",
          ] as const) {
            const p = resolved.placements[slot]
            if (!p?.url && !p?.assetId) continue
            placements[slot] = {
              id: p.assetId,
              url: p.url ?? "",
              thumbnailUrl: p.url ?? "",
              type: "image",
              category: ImageCategory.OTHER,
              color: "Default",
              selected: true,
              assetId: p.assetId,
              sourceUrl: "AI (existing variant)",
            }
          }
          swaps.set(c.id, { main, placements })
        } catch {
          /* leave the card as-is on failure */
        }
      }
      if (swaps.size === 0) return

      dispatch(
        adForgeActions.setSelectedAssets(
          selectedAssetsRef.current.map(a => swaps.get(a.id)?.main ?? a),
        ),
      )
      setPlacementAssetsByAsset(prev => {
        const next = { ...prev }
        for (const [cardId, { placements }] of swaps) {
          const merged = { ...(next[cardId] ?? {}) }
          for (const slot of Object.keys(placements) as Array<
            keyof PlacementAssets
          >) {
            if (!merged[slot]) merged[slot] = placements[slot] // never clobber a user-set slot
          }
          next[cardId] = merged
        }
        return next
      })
    })()
  }, [adAssetIdsKey])

  // Keep the carousel selector in range when cards are added/removed.
  useEffect(() => {
    if (selectedCarouselIndex >= adAssets.length && adAssets.length > 0) {
      setSelectedCarouselIndex(adAssets.length - 1)
    } else if (adAssets.length === 0 && selectedCarouselIndex !== 0) {
      setSelectedCarouselIndex(0)
    }
  }, [adAssets.length, selectedCarouselIndex])

  // Apply one-shot prefill from Ad Forge navigation
  useEffect(() => {
    if (!prefillCreative) return
    if (prefillCreative.destinationUrl) {
      setDestinationUrl(prefillCreative.destinationUrl)
    }
    if (prefillCreative.primaryText) setPrimaryText(prefillCreative.primaryText)
    if (prefillCreative.headline) setHeadline(prefillCreative.headline)
    if (prefillCreative.description) setDescription(prefillCreative.description)
    if (prefillCreative.callToAction) {
      setCallToAction(prefillCreative.callToAction)
    }
    if (prefillCreative.adName) setAdName(prefillCreative.adName)
    dispatch(adForgeActions.setPrefillCreative(undefined))
    pendingRebaselineRef.current = true
  }, [prefillCreative, dispatch])

  // Auto-fetch creative data when first asset is added and form is empty
  const [fetchAssetCreative] = useLazyGetAssetCreativeQuery()
  const prevAssetCountRef = useRef(0)
  useEffect(() => {
    if (
      selectedAssets.length === 1 &&
      prevAssetCountRef.current === 0 &&
      !destinationUrl &&
      !primaryText
    ) {
      const asset = selectedAssets[0]
      const assetId = asset.assetId || asset.id
      fetchAssetCreative(assetId)
        .unwrap()
        .then(creative => {
          if (creative.destinationUrl) {
            setDestinationUrl(creative.destinationUrl)
          }
          if (creative.primaryText) setPrimaryText(creative.primaryText)
          if (creative.headline) setHeadline(creative.headline)
          if (creative.description) setDescription(creative.description)
          if (creative.callToAction) setCallToAction(creative.callToAction)
        })
        .catch(() => {
          // Silently ignore — user can fill in manually
        })
    }
    prevAssetCountRef.current = selectedAssets.length
  }, [selectedAssets, destinationUrl, primaryText, fetchAssetCreative])

  // Debounced server-side search — keeps the picker responsive while typing
  // and avoids the 100-row Meta limit hiding name matches.
  const [debouncedSearch, setDebouncedSearch] = useState("")
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(adSetSearch.trim()), 300)
    return () => clearTimeout(id)
  }, [adSetSearch])

  // Ad-set search is still kicked off (other parts of the page read `adSets`
  // for status badges / selectedAdSets resolution), but the picker UI that
  // used the loading/fetching flags is gone — destructure just `data`.
  const { data: adSets } = useSearchAdSetsQuery({
    platform: sourceAd?.platform ?? "meta",
    ...(debouncedSearch && { search: debouncedSearch }),
  })

  // Build a lookup that merges the live search result WITH the persistent
  // cache of previously-selected ad sets. Search narrowing must never lose
  // the user's in-flight selections (e.g. "marathon" search would otherwise
  // drop any selected ad sets that don't match the query).
  const adSetsById = useMemo(() => {
    const m = new Map<string, AdSet>()
    for (const [id, a] of Object.entries(selectedAdSetsData)) m.set(id, a)
    for (const a of adSets ?? []) m.set(String(a.id), a) // live results override stale cache
    return m
  }, [adSets, selectedAdSetsData])

  // Hydrate the persistent cache whenever fresh results arrive for an id
  // that's currently selected — keeps cached metadata in sync with Meta.
  useEffect(() => {
    if (!adSets || adSets.length === 0) return
    const selectedSet = new Set(selectedAdSetIds)
    const toUpsert = adSets.filter(a => selectedSet.has(String(a.id)))
    if (toUpsert.length > 0) {
      dispatch(adForgeActions.upsertSelectedAdSetData(toUpsert))
    }
  }, [adSets, selectedAdSetIds, dispatch])

  const selectedAdSets = useMemo(
    () =>
      selectedAdSetIds
        .map(id => adSetsById.get(id))
        .filter((a): a is AdSet => Boolean(a)),
    [selectedAdSetIds, adSetsById],
  )

  const hasAnyProfileVisit = selectedAdSets.some(isProfileVisit)
  const allProfileVisit =
    selectedAdSets.length > 0 && selectedAdSets.every(isProfileVisit)

  // Auto-show the existing creative override section in source-mode (post / ad
  // blast) when at least one selected target is non-PV — those objectives
  // (TRAFFIC, CONVERSIONS, LEADS, SALES) need destinationUrl + CTA + UTM, and
  // Meta rejects the blast with subcode 2061015 otherwise. We unhide the full
  // section the form already renders (rather than building a duplicate
  // standalone block) so the user gets URL, CTA, UTM, and the rest of the
  // override fields in their established layout. When all targets are PV the
  // section stays collapsed: URL is server-forced (IG profile URL) and CTA
  // server-forced to VIEW_INSTAGRAM_PROFILE — nothing to edit.
  useEffect(() => {
    if (sourceAd && selectedAdSets.length > 0 && !allProfileVisit) {
      setShowCreativeFields(true)
    }
  }, [sourceAd, allProfileVisit, selectedAdSets.length])

  // Save-draft hook — persists the current creative to `AdCreative` without
  // launching. Lands the user in AdBlaster afterwards so they can blast it
  // (or stack more drafts before blasting in bulk).
  const [createCreative] = useAdForgeCreateCreativeMutation()
  const [updateCreative] = useAdForgeUpdateCreativeMutation()
  // Bakes assigned layouts into media at save-draft time (ad creation);
  // bakeOverlayToFile powers the card's download-with-layout action.
  const { bakeOverlay, bakeOverlayToFile } = useOverlayBaker()
  const [downloadingOverlayId, setDownloadingOverlayId] = useState<
    string | null
  >(null)

  // Bake a card's per-placement variants WITH its layout. A slot's own
  // arrangement (edited at the placement frame) wins; otherwise the base
  // layout re-solves at the variant's aspect. Slots with an arrangement but
  // no variant bake from the card's media cover-cropped to that frame.
  const bakePlacementsWithOverlay = async (
    card: ScrapedImage,
    placements: PlacementAssets | undefined,
  ): Promise<PlacementAssets> => {
    const doc = card.overlayDoc
    if (!doc) return placements ?? {}
    const out: PlacementAssets = {}
    const slots: PlacementSlotKey[] = [
      "landscape",
      "portrait",
      "stories",
      "square",
    ]
    for (const slot of slots) {
      const variant = placements?.[slot]
      const override = card.overlayDocByPlacement?.[slot]
      if (!variant && !override) continue
      const media = variant ?? card
      const slotDoc = override ?? rescaleDoc(doc, await measureMediaDims(media))
      out[slot] = await bakeOverlay({
        ...media,
        overlayDoc: slotDoc,
        overlayLayoutId: card.overlayLayoutId,
      })
    }
    return out
  }

  const downloadWithLayout = async (asset: ScrapedImage): Promise<void> => {
    if (downloadingOverlayId) return
    setDownloadingOverlayId(asset.id)
    const isVideo = asset.type === "video"
    if (isVideo) {
      toast.info("Rendering video with layout — this plays through once…", {
        autoClose: 4000,
      })
    }
    try {
      const file = await bakeOverlayToFile(asset)
      downloadBlob(file, file.name)
      toast.success("Downloaded with layout baked in")
    } catch (err) {
      const message = err instanceof Error ? err.message : "Download failed"
      toast.error(message)
    } finally {
      setDownloadingOverlayId(null)
    }
  }
  // Flips true synchronously the instant Save is clicked (before the validation
  // /serialization/mutation round-trip) so the button disables + shows "Saving…"
  // immediately, and stays true through both the create AND update (PATCH) paths.
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const navigate = useStableNavigate()

  // Edit mode: `?creative=<id>` deep-links AdForge to an existing draft. We
  // fetch it, hydrate the builder once, and PATCH on save instead of POST.
  const [searchParams] = useSearchParams()
  const editCreativeId = searchParams.get("creative")
  const {
    data: editCreative,
    isLoading: isLoadingEditCreative,
    isError: isEditCreativeError,
  } = useAdForgeGetCreativeQuery(editCreativeId ?? "", {
    skip: !editCreativeId,
  })
  // The saved display link isn't an editable field in the builder, but the
  // backend's flattenUpdate rewrites every creative sub-field when `creative`
  // is present — so we stash it and replay it on PATCH to avoid nulling it.
  const editingDisplayLinkRef = useRef<string | undefined>(undefined)
  // One-shot hydration guard so re-renders (or RTK cache refreshes) don't
  // clobber edits the user made after the initial load.
  const hydratedCreativeIdRef = useRef<string | null>(null)
  // True until the deep-linked draft has been fetched AND hydrated into the
  // builder — drives a loading overlay so the editor isn't shown blank/stale.
  // A fetch error clears it (we surface an error screen instead of spinning).
  const isHydratingEdit =
    !!editCreativeId &&
    !isEditCreativeError &&
    (isLoadingEditCreative || hydratedCreativeIdRef.current !== editCreativeId)

  // The draft's saved product selection lives as MANUAL AssetProductTags on
  // its media assets — hydrate the Feed products field from their union,
  // once per opened creative (so refetches never clobber later edits).
  const editAssetIds = useMemo(
    () => [
      ...new Set(
        (editCreative?.mediaAssets ?? [])
          .map(m => m.assetId)
          .filter((id): id is string => !!id),
      ),
    ],
    [editCreative],
  )
  const { data: editProductTags } = useBulkProductTagsQuery(
    { assetIds: editAssetIds },
    { skip: !editCreativeId || editAssetIds.length === 0 },
  )
  const hydratedProductsCreativeIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!editCreative) return
    if (hydratedProductsCreativeIdRef.current === editCreative.id) return

    // Preferred path: the detail fetch carries the saved selection directly
    // (computed server-side, including URL-only media resolved to library
    // assets — the client-side union below can't see those).
    if (editCreative.feedProducts) {
      hydratedProductsCreativeIdRef.current = editCreative.id
      // Always set — an empty selection must CLEAR chips left over from a
      // previously edited creative, or they'd get written onto this one's
      // assets on save.
      setFeedProducts(
        editCreative.feedProducts.map(p => ({
          productRefId: p.productRefId,
          title: p.title,
        })),
      )
      return
    }

    // Fallback for an API that predates `feedProducts`: union the media
    // assets' MANUAL tags client-side.
    if (!editProductTags) return
    hydratedProductsCreativeIdRef.current = editCreative.id
    const byRef = new Map<string, SelectedFeedProduct>()
    for (const tags of Object.values(editProductTags.results)) {
      for (const tag of tags) {
        if (tag.source !== "MANUAL" || byRef.has(tag.productRefId)) continue
        byRef.set(tag.productRefId, {
          productRefId: tag.productRefId,
          title: tag.productName ?? tag.productRefId,
          imageUrl: tag.productImageUrl,
        })
      }
    }
    if (byRef.size > 0) setFeedProducts([...byRef.values()])
  }, [editCreative, editProductTags])

  // The server-hydrated selection often carries only the raw productRefId as
  // its title (the Product table is sparse) — enrich those from the mega_feed
  // cube so the chips show the real product name + image.
  const unnamedFeedRefIds = useMemo(
    () =>
      feedProducts
        .filter(p => !p.title || p.title === p.productRefId)
        .map(p => p.productRefId),
    [feedProducts],
  )
  const feedNameLookupQuery = useMemo<CubeQuery>(
    () => ({
      dimensions: [
        "mega_feed.item_group_id",
        "mega_feed.parent_title",
        "mega_feed.image_link",
      ],
      filters: [
        {
          member: "mega_feed.item_group_id",
          operator: "equals",
          values: unnamedFeedRefIds,
        },
      ],
      limit: Math.max(unnamedFeedRefIds.length, 1),
    }),
    [unnamedFeedRefIds],
  )
  const { resultSet: feedNameLookupResult } = useCubeQuery(
    feedNameLookupQuery,
    { skip: unnamedFeedRefIds.length === 0 },
  )
  useEffect(() => {
    if (!feedNameLookupResult) return
    const byRef = new Map<string, { title: string; imageUrl?: string }>()
    for (const row of feedNameLookupResult.rawData() as Record<
      string,
      unknown
    >[]) {
      const refId = String(row["mega_feed.item_group_id"] ?? "").trim()
      const title = String(row["mega_feed.parent_title"] ?? "").trim()
      if (!refId || !title || byRef.has(refId)) continue
      byRef.set(refId, {
        title,
        imageUrl: String(row["mega_feed.image_link"] ?? "").trim() || undefined,
      })
    }
    if (byRef.size === 0) return
    // Keep the previous array reference when nothing changes — the memo
    // above feeds the cube query, so a gratuitous new array would refetch.
    setFeedProducts(prev => {
      let changed = false
      const next = prev.map(p => {
        const hit = byRef.get(p.productRefId)
        if (!hit || (p.title && p.title !== p.productRefId)) return p
        changed = true
        return { ...p, title: hit.title, imageUrl: p.imageUrl ?? hit.imageUrl }
      })
      return changed ? next : prev
    })
  }, [feedNameLookupResult])

  // Hydrate the builder from the fetched creative exactly once per id.
  useEffect(() => {
    if (!editCreative || hydratedCreativeIdRef.current === editCreative.id) {
      return
    }
    hydratedCreativeIdRef.current = editCreative.id
    editingDisplayLinkRef.current = editCreative.displayLink ?? undefined
    setTemplate(editCreative.format === "CAROUSEL" ? "carousel" : "single")
    setPrimaryText(editCreative.primaryText ?? "")
    setHeadline(editCreative.headline ?? "")
    setDescription(editCreative.description ?? "")
    setDestinationUrl(editCreative.destinationUrl ?? "")
    setCallToAction(editCreative.callToAction ?? "LEARN_MORE")
    setAdName(editCreative.name ?? "")
    setAdTags(editCreative.tags ?? {})
    setShowCreativeFields(true)
    const tp = editCreative.tracking?.urlParameters
    if (tp && Object.keys(tp).length) {
      setTracking(
        Object.entries(tp).map(([key, value]) => ({
          key,
          value: String(value),
        })),
      )
    }
    const assets = (editCreative.mediaAssets ?? []).map(
      mediaAssetToScrapedImage,
    )
    draftAssetIdRef.current = assets[0]?.assetId
    dispatch(adForgeActions.setSelectedAssets(assets))
    const byAsset: Record<string, PlacementAssets> = {}
    if (editCreative.format === "CAROUSEL") {
      const byCard = editCreative.placementAssetsByCard ?? {}
      for (const asset of assets) {
        const rec = byCard[asset.assetId ?? asset.id]
        if (rec) byAsset[asset.id] = deserializePlacements(rec)
      }
    } else if (editCreative.placementAssets && assets[0]) {
      byAsset[assets[0].id] = deserializePlacements(
        editCreative.placementAssets,
      )
    }
    setPlacementAssetsByAsset(byAsset)
    // Stage the saved per-placement variants in the asset pool too. They're
    // part of the ad, so the drawer should list them (marked with their slot
    // via `placementLabelsByAssetId`) instead of the user re-opening a draft
    // and seeing only the base image staged.
    for (const variants of Object.values(byAsset)) {
      for (const slot of PLACEMENT_SLOT_KEYS) {
        const variant = variants[slot]
        if (variant) dispatch(adForgeActions.addPickedAsset(variant))
      }
    }
    pendingRebaselineRef.current = true
  }, [editCreative, dispatch])

  // ── Unsaved-changes guard ─────────────────────────────────────────────────
  // Serialize everything the user can edit; compare against a baseline to know
  // whether leaving would lose work. The baseline starts as the empty form and
  // re-snaps after an edit-mode hydrate and after a successful save.
  const formSnapshot = useMemo(
    () =>
      JSON.stringify({
        template,
        headline,
        primaryText,
        description,
        destinationUrl,
        callToAction,
        adName,
        adTags,
        tracking,
        feedProducts: feedProducts.map(p => p.productRefId),
        placements: placementAssetsByAsset,
        assets: selectedAssets.map(a => a.assetId ?? a.url),
      }),
    [
      template,
      headline,
      primaryText,
      description,
      destinationUrl,
      callToAction,
      adName,
      adTags,
      tracking,
      feedProducts,
      placementAssetsByAsset,
      selectedAssets,
    ],
  )
  const baselineSnapshotRef = useRef(formSnapshot)
  const [isFormDirty, setIsFormDirty] = useState(false)

  useEffect(() => {
    if (pendingRebaselineRef.current) {
      pendingRebaselineRef.current = false
      baselineSnapshotRef.current = formSnapshot
      setIsFormDirty(false)
      return
    }
    setIsFormDirty(formSnapshot !== baselineSnapshotRef.current)
  }, [formSnapshot])

  // Source mode (cloning an existing ad/post) is a launch-prefill flow whose
  // CTA returns to AdBlaster — there's no draft to lose, so don't guard it.
  const blocker = useUnsavedChangesPrompt(isFormDirty && !sourceAd)

  // ── DnD: drag from drawer pool → slot, slot ↔ slot, slot → drawer ─────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  )

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id)
    setDraggingFromPool(id.startsWith(DRAWER_ITEM_PREFIX))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggingFromPool(false)
    const { active, over } = event
    if (!over) return
    const activeId = String(active.id)
    const overId = String(over.id)
    const limit = template === "single" ? 1 : CAROUSEL_SLOT_COUNT

    // POOL → slot OR placement-tile. Duplicates are allowed (same asset
    // can fill multiple carousel cards), so we never dedupe — drops only
    // replace or append. Items in the Top Ads / Top Posts / Top Products
    // tabs are NOT yet in `pickedAssets`, so we read the full asset payload
    // off the drag event itself.
    if (activeId.startsWith(DRAWER_ITEM_PREFIX)) {
      const data = active.data.current as
        | { kind: "pool"; asset: ScrapedImage }
        | undefined
      const dropped = data?.asset
      if (!dropped) return

      // POOL → placement tile (right-hand showcase). Sets the per-placement
      // variant for the targeted card+slot, without touching the carousel
      // selection itself.
      if (overId.startsWith(PLACEMENT_DROPPABLE_PREFIX)) {
        const payload = overId.slice(PLACEMENT_DROPPABLE_PREFIX.length)
        const sepIdx = payload.lastIndexOf(":")
        if (sepIdx <= 0) return
        const cardId = payload.slice(0, sepIdx)
        const slot = payload.slice(sepIdx + 1) as
          | "landscape"
          | "portrait"
          | "stories"
          | "square"
        setPlacementAssetsByAsset(prev => ({
          ...prev,
          [cardId]: { ...(prev[cardId] || {}), [slot]: dropped },
        }))
        return
      }

      if (!overId.startsWith(SLOT_DROPPABLE_PREFIX)) return
      const slotIndex = Number(overId.slice(SLOT_DROPPABLE_PREFIX.length))
      const next = [...selectedAssets]
      if (slotIndex < next.length) {
        // Replace whatever is in this slot.
        next[slotIndex] = dropped
      } else if (next.length < limit) {
        // Pack to the next available slot.
        next.push(dropped)
      } else {
        return
      }
      dispatch(adForgeActions.setSelectedAssets(next.slice(0, limit)))
      return
    }

    // SLOT → slot (swap/reorder) or SLOT → pool (remove from ad)
    if (activeId.startsWith(SLOT_ITEM_PREFIX)) {
      const fromIndex = Number(activeId.slice(SLOT_ITEM_PREFIX.length))
      if (overId === DRAWER_DROPPABLE_ID) {
        if (fromIndex < 0 || fromIndex >= selectedAssets.length) return
        const next = selectedAssets.filter((_, i) => i !== fromIndex)
        dispatch(adForgeActions.setSelectedAssets(next))
        return
      }
      if (!overId.startsWith(SLOT_DROPPABLE_PREFIX)) return
      const toIndex = Number(overId.slice(SLOT_DROPPABLE_PREFIX.length))
      if (fromIndex === toIndex) return
      const next = [...selectedAssets]
      const moved = next[fromIndex]
      if (!moved) return
      if (toIndex < next.length) {
        // Swap with the target slot's asset (preserves both in the ad).
        next[fromIndex] = next[toIndex]
        next[toIndex] = moved
      } else {
        // Dropping onto an empty trailing slot — move to end.
        next.splice(fromIndex, 1)
        next.push(moved)
      }
      dispatch(adForgeActions.setSelectedAssets(next))
    }
  }

  // Upload handlers
  const handleMainUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : []
    e.target.value = ""
    if (files.length === 0) return
    // A feed-seeded session attributes manual uploads to its product, so they
    // land in that product's feed pool alongside the AI output.
    const uploaded = await uploadFiles(
      files,
      productContext?.productId
        ? { productRefId: productContext.productId }
        : undefined,
    )
    for (const img of uploaded) {
      dispatch(adForgeActions.addSelectedAsset(img))
    }
    analyzeNewAssets(uploaded)
    toast.success(
      `${uploaded.length} asset${uploaded.length > 1 ? "s" : ""} uploaded`,
    )
  }

  /**
   * Save the current builder state as an `AdCreative` draft and navigate to
   * the AdBlaster Creatives tab. Doesn't launch — that's handled by the
   * AdBlaster bulk-blast modal. The DTO shape matches the launch payload the
   * blast endpoint expects, so a saved draft can be blasted as-is.
   */
  const handleSaveDraft = async ({
    navigateAfter = true,
  }: { navigateAfter?: boolean } = {}): Promise<boolean> => {
    if (selectedAssets.length === 0) {
      toast.warning("Add at least one asset before saving")
      return false
    }
    if (!primaryText.trim()) {
      toast.warning("Primary text is required to save a draft")
      return false
    }
    if (!headline.trim()) {
      toast.warning("Headline is required to save a draft")
      return false
    }
    // Tags are mandatory: an untagged ad can't be sliced by any of the
    // dimensions the company reports on, and once it's live the only way back
    // is re-inferring tags from its name. Names the specific gaps rather than
    // just refusing.
    if (tagRequirement) {
      toast.warning(
        tagRequirement.needsAnyTag
          ? 'Add tags before saving — use "Build from tags" next to Ad Name.'
          : `Set required tags before saving: ${tagRequirement.missing
              .map(labelForSlug)
              .join(", ")}`,
      )
      setShowAdNameBuilder(true)
      return false
    }

    // Validation passed → show the saving state immediately (synchronous), so
    // the button disables before the serialization + save round-trip.
    setIsSavingDraft(true)

    // Cards carry assigned layouts NON-destructively while editing — ad
    // creation is the moment they become pixels. Bake each overlaid card's
    // media now (client-side composite + upload) and save the baked assets.
    let effectiveAssets = adAssets
    const overlaidCards = adAssets.filter(a => a.overlayDoc)
    if (overlaidCards.length > 0) {
      try {
        const bakedByCardId = new Map<string, ScrapedImage>()
        for (const card of overlaidCards) {
          toast.info(
            `Applying layout to ${card.type === "video" ? "video" : "image"}…`,
            { autoClose: 2000 },
          )
          bakedByCardId.set(card.id, await bakeOverlay(card))
        }
        effectiveAssets = adAssets.map(a => bakedByCardId.get(a.id) ?? a)
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to apply layout"
        toast.error(message)
        setIsSavingDraft(false)
        return false
      }
    }

    // Save exactly the cards the UI renders — `adAssets` is `selectedAssets`
    // capped to the template limit (1 for single, 10 for carousel). Mapping raw
    // `selectedAssets` here would persist phantom cards beyond the 10 shown
    // (the pool can hold more than the carousel cap), and a >10-card carousel is
    // rejected by Meta with "(#105) child_attachments has too many elements".
    const hasVideo = effectiveAssets.some(a => a.type === "video")
    const format: "SINGLE_IMAGE" | "SINGLE_VIDEO" | "CAROUSEL" =
      template === "carousel"
        ? "CAROUSEL"
        : hasVideo
          ? "SINGLE_VIDEO"
          : "SINGLE_IMAGE"

    const mediaAssets = effectiveAssets.map(asset => ({
      type: asset.type === "video" ? ("video" as const) : ("image" as const),
      url: asset.url,
      ...(asset.assetId && { assetId: asset.assetId }),
      ...(asset.thumbnailUrl && { thumbnailUrl: asset.thumbnailUrl }),
      ...(asset.width && { width: asset.width }),
      ...(asset.height && { height: asset.height }),
    }))

    // Per-placement crops/variants. Carousel keys each card's overrides by the
    // card's assetId (falling back to its local id); single uses the flat
    // `placementAssets` slot. Both are omitted when empty so the DTO stays lean.
    let placementAssets: Record<string, AdCreativeMediaAsset> | undefined
    let placementAssetsByCard:
      | Record<string, Record<string, AdCreativeMediaAsset>>
      | undefined
    try {
      if (template === "carousel") {
        const byCard: Record<string, Record<string, AdCreativeMediaAsset>> = {}
        for (const [cardIndex, card] of adAssets.entries()) {
          let cardPlacements = placementAssetsByAsset[card.id]
          // Overlaid cards bake each placement at ITS ratio (per-slot
          // arrangements included) — the pre-overlay variants must never
          // ship (they'd launch placements without the layout).
          if (card.overlayDoc) {
            cardPlacements = await bakePlacementsWithOverlay(
              card,
              cardPlacements,
            )
          }
          const ser = serializePlacements(cardPlacements)
          if (ser) {
            const saved = effectiveAssets[cardIndex]
            byCard[saved?.assetId ?? card.assetId ?? card.id] = ser
          }
        }
        if (Object.keys(byCard).length) placementAssetsByCard = byCard
      } else if (adAssets[0]) {
        let cardPlacements = placementAssetsByAsset[adAssets[0].id]
        if (adAssets[0].overlayDoc) {
          cardPlacements = await bakePlacementsWithOverlay(
            adAssets[0],
            cardPlacements,
          )
        }
        placementAssets = serializePlacements(cardPlacements)
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to apply layout"
      toast.error(message)
      setIsSavingDraft(false)
      return false
    }

    // Tracking pairs → urlParameters object, mirroring the launch path.
    const urlParameters: Record<string, string> = {}
    for (const { key, value } of tracking) {
      const k = key.trim()
      const v = value.trim()
      if (k && v) urlParameters[k] = v
    }

    const draftName =
      adName.trim() ||
      `${template === "carousel" ? "Carousel" : "Single"} — ${
        headline || primaryText.slice(0, 32) || "Untitled draft"
      }`

    const creative = {
      primaryText,
      headline: headline || undefined,
      description: description || undefined,
      destinationUrl: destinationUrl.trim() || undefined,
      callToAction,
      mediaAssets,
      ...(placementAssets && { placementAssets }),
      ...(placementAssetsByCard && { placementAssetsByCard }),
      // Replay the saved displayLink on edit so the backend's flattenUpdate
      // (which rewrites every creative sub-field) doesn't null it out.
      ...(editingDisplayLinkRef.current && {
        displayLink: editingDisplayLinkRef.current,
      }),
    }
    const trackingPayload =
      Object.keys(urlParameters).length > 0
        ? { tracking: { urlParameters } }
        : {}

    try {
      if (editCreativeId) {
        const result = await updateCreative({
          id: editCreativeId,
          // Always sent (even when empty) so clearing the tag selection on an
          // existing draft actually clears it — the backend treats an absent
          // `tags` as "keep what's stored".
          body: {
            name: draftName,
            format,
            creative,
            tags: adTags,
            // Always sent (like `tags`) so clearing the selection actually
            // clears the assets' MANUAL feed tags on save.
            productRefIds: feedProducts.map(p => p.productRefId),
            ...trackingPayload,
          },
        }).unwrap()
        toast.success(`Updated "${result.name}"`)
      } else {
        const result = await createCreative({
          name: draftName,
          format,
          creative,
          tags: adTags,
          productRefIds: feedProducts.map(p => p.productRefId),
          ...trackingPayload,
        }).unwrap()
        toast.success(`Saved "${result.name}" to AdBlaster`)
      }
      // The saved form is now the clean baseline, so the unsaved-changes guard
      // won't fire on the redirect below (or on a subsequent "leave anyway").
      baselineSnapshotRef.current = formSnapshot
      setIsFormDirty(false)
      if (navigateAfter) {
        blocker.allowNextNavigation()
        navigate(brandRoutes.AD_BLASTER.CREATIVES)
      }
      return true
    } catch (err) {
      const message =
        err &&
        typeof err === "object" &&
        "data" in err &&
        err.data &&
        typeof err.data === "object" &&
        "message" in err.data
          ? String(err.data.message)
          : "Failed to save draft"
      toast.error(message)
      return false
    } finally {
      setIsSavingDraft(false)
    }
  }

  // (Campaign grouping / filteredCampaigns memos removed — they only fed
  // the deleted AdSetPicker. Ad-set selection lives in AdBlaster now.)

  // Ad-set selection lives in AdBlaster now — AdForge is the creative editor.
  // The picker modal + destination sidebar are gone; users who want to blast
  // pick targets in AdBlaster's BlastTargetPickerModal (Creatives / Posts /
  // Ads tabs). AdForge's job is to produce a draft creative (`Save as draft`)
  // or, in source mode, return the user to AdBlaster once they're done
  // editing.

  // Edit mode: hold the builder behind a loading screen until the deep-linked
  // draft is fetched and hydrated, so the form never flashes blank/stale.
  if (isHydratingEdit) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 text-system-gray-400">
        <span className="loading loading-spinner loading-lg text-brand" />
        <p className="text-sm">Loading draft…</p>
      </div>
    )
  }

  // Edit mode: the deep-linked draft couldn't be loaded (deleted, or a network
  // error) — don't drop the user into a blank editor that would overwrite it.
  if (editCreativeId && isEditCreativeError) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 text-system-gray-400">
        <ExclamationTriangleIcon className="w-8 h-8 text-warning" />
        <p className="text-sm">Couldn’t load this draft.</p>
        <button
          type="button"
          onClick={() => navigate(brandRoutes.AD_BLASTER.CREATIVES)}
          className="btn btn-sm btn-primary"
        >
          Back to creatives
        </button>
      </div>
    )
  }

  // Builder — shown after at least one destination selected
  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        setDraggingFromPool(false)
      }}
    >
      {/* Two-column row: the builder scrolls on the left, the Selected Assets
          panel is a permanent, independently-scrolling right column. `h-full`
          (not flex-1) because the routed parent is a block, not a flex
          container — so we fill its definite height directly. */}
      <div className="h-full flex min-h-0 overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white">Ad Forge</h1>
                <p className="text-[11px] text-system-gray-500 mt-0.5">
                  {sourceAd
                    ? "Editing creative — destinations are picked in AdBlaster"
                    : "Build a creative draft — pick destinations in AdBlaster"}
                </p>
              </div>
              {/* Upload + Add Assets buttons used to live here, but the same
                actions are now reachable from the Selected Assets drawer
                header — removed to declutter the page chrome. The hidden
                file input below is still mounted so the drawer's Upload
                button can click() it through `mainUploadInputRef`. */}
              {!sourceAd && (
                <input
                  ref={mainUploadInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleMainUpload}
                  className="hidden"
                />
              )}
            </div>

            {/* Destinations bar + in-page blast result removed — ad-set
              selection + blast results both live in AdBlaster now
              (BlastTargetPickerModal). AdForge is the creative editor only. */}

            {/* Reviewer feedback — the reason this draft came back. Shown while
              editing a creative in CHANGES_REQUESTED / REJECTED so the person
              fixing it sees WHAT to change without hunting through lists. */}
            {editCreative &&
              (editCreative.approvalStatus === "CHANGES_REQUESTED" ||
                editCreative.approvalStatus === "REJECTED") && (
                <div className="flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3">
                  <span className="mt-0.5 shrink-0 rounded-full bg-amber-500/20 px-2 py-0.5 text-[11px] font-medium text-amber-300">
                    {editCreative.approvalStatus === "REJECTED"
                      ? "Rejected"
                      : "Changes requested"}
                  </span>
                  <div className="min-w-0 text-sm text-amber-100">
                    {editCreative.reviewNote ? (
                      <p className="whitespace-pre-wrap">
                        “{editCreative.reviewNote}”
                      </p>
                    ) : (
                      <p className="italic text-amber-200/70">
                        The reviewer didn&apos;t leave a note.
                      </p>
                    )}
                    {editCreative.reviewedAt && (
                      <p className="mt-0.5 text-[11px] text-amber-200/60">
                        Reviewed{" "}
                        {new Date(editCreative.reviewedAt).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              )}

            <div className="grid grid-cols-1 lg:grid-cols-7 gap-8">
              {/* LEFT: Builder — takes full width (col-span-7) ONLY when the
                placements showcase on the right is hidden (source mode +
                blast-as-is; placement preview irrelevant). When the
                placements column renders (builder mode, or source mode with
                showCreativeFields=true because the user is editing or
                because a non-PV target auto-expanded the section), the form
                drops back to col-span-5 to leave room for the showcase. */}
              <div
                className={
                  sourceAd && !showCreativeFields
                    ? "lg:col-span-7 space-y-6"
                    : "lg:col-span-5 space-y-6"
                }
              >
                {hasAnyProfileVisit && (
                  <div className="bg-brand/5 border border-brand/15 rounded-lg px-3 py-2">
                    <p className="text-[11px] text-system-gray-400">
                      {allProfileVisit
                        ? "All destinations are Profile Visit ad sets — URL and CTA will auto-target the IG profile."
                        : "Some destinations are Profile Visit ad sets — their URL and CTA will auto-target the IG profile (shared values are ignored for those)."}
                    </p>
                  </div>
                )}

                {hasAnyProfileVisit && template === "carousel" && (
                  <div className="bg-system-error-500/10 border border-system-error-500/30 rounded-lg px-3 py-2">
                    <p className="text-[11px] text-system-error-400">
                      Carousel is not supported for Profile Visit ad sets (Meta
                      #1346001). Switch to <strong>Single Asset</strong> or
                      remove the Profile Visit destinations before launching.
                    </p>
                  </div>
                )}

                {sourceAd && (
                  <div className="rounded-xl border border-system-gray-800/50 bg-system-gray-900/30 p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <p className="text-[10px] uppercase tracking-wider text-system-gray-500">
                        {isSourcePost
                          ? "Source Post — will be promoted as an ad"
                          : "Source Ad — creative will be cloned"}
                      </p>
                      <button
                        onClick={() =>
                          dispatch(adForgeActions.setSourceAd(undefined))
                        }
                        className="text-[10px] text-system-gray-500 hover:text-white"
                        title={
                          isSourcePost
                            ? "Discard source post and build a fresh ad"
                            : "Discard source ad and build a fresh ad"
                        }
                      >
                        Clear source
                      </button>
                    </div>
                    <div className="flex items-start gap-4">
                      <div
                        className={cn(
                          "w-20 h-20 rounded-lg overflow-hidden bg-system-gray-800 flex-shrink-0",
                          isDemoEnabled && sourceAd.imageUrl && "blur-md",
                        )}
                      >
                        {sourceAd.imageUrl ? (
                          <img
                            src={sourceAd.imageUrl}
                            alt={sourceAd.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <PhotoIcon className="w-7 h-7 text-system-gray-600" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p
                          className={cn(
                            "text-sm font-semibold text-white truncate",
                            isDemoEnabled && "blur-md",
                          )}
                        >
                          {sourceAd.name}
                        </p>
                        <p className="text-[11px] text-system-gray-500 mt-1">
                          {sourceAd.mediaChannel ||
                            (sourceAd.platform === "tiktok"
                              ? "TikTok"
                              : "Meta")}
                        </p>
                        <p className="text-[10px] text-system-gray-600 mt-1.5 font-mono break-all">
                          ID: {sourceRefId}
                        </p>
                        {(isFetchingSource || isResolvingAsset) && (
                          <p className="text-[10px] text-system-gray-500 mt-2 flex items-center gap-1.5">
                            <span className="loading loading-spinner loading-xs text-brand" />
                            {isFetchingSource && isResolvingAsset
                              ? "Loading creative & media…"
                              : isFetchingSource
                                ? "Loading creative…"
                                : "Resolving media…"}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {sourceAd && requiredFields.length > 0 && (
                  <div className="bg-system-warning-500/10 border border-system-warning-500/30 rounded-lg px-3 py-2">
                    <p className="text-[11px] text-system-warning-400">
                      Destination requires:{" "}
                      <strong>{requiredFields.join(", ")}</strong>. Fill below
                      and retry.
                    </p>
                  </div>
                )}

                {/* Blast error banner removed — blast results live in
                  AdBlaster's BlastTargetPickerModal now. */}

                {/* Template Selector — hidden in source-ad mode (creative cloned from source) */}
                {!sourceAd && (
                  <>
                    {/* Template Selector */}
                    <div className="flex gap-3">
                      {(
                        [
                          { value: "single" as const, label: "Single Asset" },
                          { value: "carousel" as const, label: "Carousel" },
                        ] as const
                      ).map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setTemplate(opt.value)}
                          className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${
                            template === opt.value
                              ? "bg-brand/15 text-brand border border-brand/30"
                              : "bg-system-gray-800/40 text-system-gray-500 border border-transparent hover:text-white hover:bg-system-gray-800"
                          }`}
                        >
                          {opt.label}
                          {opt.value === "carousel" && " (up to 10)"}
                        </button>
                      ))}
                    </div>

                    {/* Create with AI — permanently visible above the asset
                      section (no longer hidden behind the per-card ✨ button;
                      that button now just picks the panel's source asset). */}
                    <AiActionDialog
                      asset={aiActionAsset}
                      actionRequest={aiActionRequest}
                      onSelectAsset={asset => {
                        // Same AVIF gate as the ✨ button — drop-zone drags
                        // land here too.
                        if (isAvifImage(asset)) {
                          toast(
                            "AVIF images can't be enhanced — re-upload as JPEG or PNG.",
                          )
                          return
                        }
                        setAiActionAsset(asset)
                      }}
                      stagedAssets={adAssets}
                      poolAssets={pickedAssets}
                      onClose={() => setAiActionAsset(null)}
                      onActionChange={a => setIsAiFlowOpen(a !== null)}
                      onGenerated={async (file, uploadOptions) => {
                        // Forward uploadOptions so AI-generated assets
                        // land in the DB tagged as GEN_AI + linked to
                        // the source via parentAssetId.
                        const [img] = await uploadFiles([file], uploadOptions)
                        if (!img) return
                        dispatch(adForgeActions.addPickedAsset(img))
                        analyzeNewAssets([img])
                        // If the AI action targeted a card that's currently
                        // selected (single template, or any carousel card),
                        // swap that card in place so the enhanced/edited image
                        // shows on the card immediately. Without this the
                        // result only lands in the pool and the card looks
                        // unchanged — which is why per-card carousel "enhance"
                        // appeared to do nothing.
                        if (
                          aiActionAsset &&
                          selectedAssets.some(a => a.id === aiActionAsset.id)
                        ) {
                          dispatch(
                            adForgeActions.setSelectedAssets(
                              selectedAssets.map(a =>
                                a.id === aiActionAsset.id ? img : a,
                              ),
                            ),
                          )
                        }
                      }}
                      onAssetReady={asset => {
                        // Motion video flow persists server-side and hands
                        // us the already-saved asset; nothing to upload.
                        // Use addSelectedAsset so each newly-generated
                        // variant lands in the pool AND auto-selected — the
                        // user explicitly asked for AI motion outputs.
                        dispatch(adForgeActions.addSelectedAsset(asset))
                        analyzeNewAssets([asset])
                      }}
                      onUseAsCarouselAd={cards => {
                        // Generated carousel cards become the ad itself:
                        // switch to the carousel template and fill the slots
                        // with the set in order (also staging them in the
                        // pool so they show in the drawer).
                        setTemplate("carousel")
                        cards.forEach(card =>
                          dispatch(adForgeActions.addPickedAsset(card)),
                        )
                        dispatch(
                          adForgeActions.setSelectedAssets(cards.slice(0, 10)),
                        )
                      }}
                    />

                    {/* Layout assignment — card brush button opens a picker;
                        the layout is stored on the card and shown as a LIVE
                        overlay. Baking happens at save-draft time. */}
                    <ApplyLayoutDialog
                      asset={applyLayoutAsset}
                      onClose={() => setApplyLayoutAsset(null)}
                      onAssign={(doc, layoutId) => {
                        if (!applyLayoutAsset) return
                        dispatch(
                          adForgeActions.setSelectedAssets(
                            selectedAssets.map(a =>
                              a.id === applyLayoutAsset.id
                                ? {
                                    ...a,
                                    overlayDoc: doc,
                                    overlayLayoutId: layoutId,
                                  }
                                : a,
                            ),
                          ),
                        )
                      }}
                      onRemove={() => {
                        if (!applyLayoutAsset) return
                        dispatch(
                          adForgeActions.setSelectedAssets(
                            selectedAssets.map(a =>
                              a.id === applyLayoutAsset.id
                                ? {
                                    ...a,
                                    overlayDoc: undefined,
                                    overlayLayoutId: undefined,
                                    overlayDocByPlacement: undefined,
                                  }
                                : a,
                            ),
                          ),
                        )
                      }}
                    />

                    {/* Video thumbnail picker — scrub to a frame, save it as
                        the asset's thumbnail (what Meta shows before play). */}
                    {thumbnailPickerAsset && (
                      <VideoThumbnailPickerModal
                        asset={thumbnailPickerAsset}
                        onClose={() => setThumbnailPickerAsset(null)}
                        onSaved={thumbnailUrl => {
                          // Refresh the card in place so the slot poster and
                          // any draft saved from here carry the new frame.
                          dispatch(
                            adForgeActions.updateSelectedAssetById({
                              id: thumbnailPickerAsset.id,
                              updates: { thumbnailUrl },
                            }),
                          )
                        }}
                      />
                    )}

                    {/* Full-page placement preview — expand button on each
                        tile; ←/→ flips through all four placements. */}
                    <PlacementPreviewModal
                      target={placementPreviewTarget}
                      placements={
                        placementPreviewTarget
                          ? placementAssetsByAsset[
                              placementPreviewTarget.card.id
                            ]
                          : undefined
                      }
                      onClose={() => setPlacementPreviewTarget(null)}
                    />

                    {/* Per-placement arranging — the tile's brush button
                        opens the layout editor at that placement's frame. */}
                    <PlacementOverlayEditorModal
                      target={placementOverlayTarget}
                      onClose={() => setPlacementOverlayTarget(null)}
                      onSave={(slot, doc) => {
                        const cardId = placementOverlayTarget?.card.id
                        if (!cardId) return
                        dispatch(
                          adForgeActions.setSelectedAssets(
                            selectedAssets.map(a =>
                              a.id === cardId
                                ? {
                                    ...a,
                                    overlayDocByPlacement: {
                                      ...a.overlayDocByPlacement,
                                      [slot]: doc,
                                    },
                                  }
                                : a,
                            ),
                          ),
                        )
                      }}
                      onReset={slot => {
                        const cardId = placementOverlayTarget?.card.id
                        if (!cardId) return
                        dispatch(
                          adForgeActions.setSelectedAssets(
                            selectedAssets.map(a => {
                              if (a.id !== cardId) return a
                              const next = { ...a.overlayDocByPlacement }
                              delete next[slot]
                              return {
                                ...a,
                                overlayDocByPlacement: Object.keys(next).length
                                  ? next
                                  : undefined,
                              }
                            }),
                          ),
                        )
                      }}
                    />

                    {/* Ad card slots — droppable targets for assets dragged from the
                    Selected Assets drawer on the right. Hidden while an AI
                    flow is open — the flow UI takes the section's place. */}
                    {!isAiFlowOpen && (
                      <section>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-white">
                            {template === "carousel"
                              ? "Carousel Cards"
                              : "Asset"}
                            <span className="ml-2 text-[11px] font-normal text-system-gray-500">
                              {adAssets.length}/{template === "single" ? 1 : 10}{" "}
                              filled
                              {pickedAssets.length > 0 && (
                                <>
                                  {" · "}
                                  {pickedAssets.length} staged
                                </>
                              )}
                            </span>
                          </h3>
                        </div>

                        {/* Bad aspect ratio warning for carousel videos */}
                        {template === "carousel" &&
                          adAssets.some(a => {
                            if (
                              a.type !== "video" ||
                              a.width == null ||
                              a.height == null
                            ) {
                              return false
                            }
                            return a.width / a.height > 1.05
                          }) && (
                            <div className="bg-system-error-500/10 border border-system-error-500/20 rounded-lg px-3 py-2 mb-2">
                              <p className="text-[11px] text-system-error-400">
                                Some videos are landscape (wider than tall).
                                Carousel requires 1:1 or 3:4 (portrait) videos.
                              </p>
                            </div>
                          )}

                        <AdCardSlots
                          template={template}
                          adAssets={adAssets}
                          isDemoEnabled={isDemoEnabled}
                          draggingFromPool={draggingFromPool}
                          activeCarouselIndex={selectedCarouselIndex}
                          enhancingAssetIds={enhancingAssetIds}
                          loadingAssetIds={prefillingAssetIds}
                          onClearSlot={slotIndex =>
                            // Index-based — duplicates of the same asset across
                            // multiple slots should NOT clear together.
                            dispatch(
                              adForgeActions.setSelectedAssets(
                                selectedAssets.filter(
                                  (_, i) => i !== slotIndex,
                                ),
                              ),
                            )
                          }
                          onOpenAi={asset => {
                            // AVIF can't go through the image-edit pipeline —
                            // don't even select it as an AI source.
                            if (isAvifImage(asset)) {
                              toast(
                                "AVIF images can't be enhanced — re-upload as JPEG or PNG.",
                              )
                              return
                            }
                            setAiActionAsset(asset)
                          }}
                          onOpenOverlay={asset => {
                            // Direct apply — a picker dialog, no canvas-flow
                            // detour. Videos and AVIF are fine (renders
                            // in-browser, no sharp pipeline involved).
                            setApplyLayoutAsset(asset)
                          }}
                          onDownloadOverlay={asset =>
                            void downloadWithLayout(asset)
                          }
                          onPickThumbnail={asset =>
                            void handlePickThumbnail(asset)
                          }
                          onSelectCarouselCard={setSelectedCarouselIndex}
                        />
                      </section>
                    )}
                  </>
                )}

                {/* Audience forecast — how the comments will most likely go,
                  from the brand's similar past creatives; keyed on the first
                  asset of the ad (builder mode only: a sourced ad/post is
                  blasted as-is). */}
                {!sourceAd && forecastAssetId && (
                  <CreativeForecastPanel
                    assetId={forecastAssetId}
                    onAnalysisAvailable={refreshAssetTagSuggestions}
                  />
                )}

                {/* In source mode, expose an "Edit creative" toggle so users
                  can blast the post/ad as-is by default. When collapsed,
                  the form below is hidden and the launch button sends only
                  the source id (no overrides). Builder mode (no sourceAd)
                  always shows the fields. */}
                {sourceAd && !showCreativeFields && (
                  <button
                    onClick={() => setShowCreativeFields(true)}
                    className="text-xs text-system-gray-400 hover:text-white underline-offset-4 hover:underline transition-colors"
                  >
                    Edit creative (optional)
                  </button>
                )}

                {/* Text Fields */}
                <section
                  aria-busy={isPrefillingSource}
                  hidden={!!sourceAd && !showCreativeFields}
                  className={`space-y-4 transition-opacity ${
                    isPrefillingSource ? "opacity-60 pointer-events-none" : ""
                  }`}
                >
                  <div>
                    <label className="text-xs font-medium text-system-gray-400 mb-1.5 flex items-center justify-between">
                      <span>
                        Ad Name
                        {/* Tags ride along with the name and are mandatory, so
                            the requirement is flagged on the field that carries
                            them rather than in a separate section. */}
                        {tagRequirement && (
                          <span className="text-system-red-400"> + tags *</span>
                        )}
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowAdNameBuilder(true)}
                        disabled={isPrefillingSource}
                        className={`btn btn-xs btn-ghost gap-1 disabled:opacity-50 ${
                          tagRequirement ? "text-system-red-400" : "text-brand"
                        }`}
                        title="Compose the ad name from this company's tags"
                      >
                        <TagIcon className="w-3 h-3" /> Build from tags
                      </button>
                    </label>
                    <input
                      type="text"
                      value={adName}
                      onChange={e => setAdName(e.target.value)}
                      disabled={isPrefillingSource}
                      placeholder={
                        selectedAdSetIds.length > 1
                          ? "Auto-generated if empty (suffixed with destination name)"
                          : "Auto-generated if empty"
                      }
                      className="input input-md w-full bg-system-gray-900/50 border-system-gray-700/50 text-white placeholder:text-system-gray-600 focus:border-brand focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                    <AdNameTagSummary
                      tags={adTags}
                      onEdit={() => setShowAdNameBuilder(true)}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-medium text-system-gray-400 mb-1.5 flex items-center justify-between">
                      <span>Primary Text *</span>
                      <span className="text-system-gray-600">
                        {primaryText.length}/2200
                      </span>
                    </label>
                    <textarea
                      value={primaryText}
                      onChange={e => setPrimaryText(e.target.value)}
                      disabled={isPrefillingSource}
                      placeholder="The main ad copy shown above the media"
                      rows={3}
                      maxLength={2200}
                      className="textarea textarea-md w-full bg-system-gray-900/50 border-system-gray-700/50 text-white placeholder:text-system-gray-600 focus:border-brand focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-system-gray-400 mb-1.5 flex items-center justify-between">
                        <span>Headline *</span>
                        <span className="text-system-gray-600">
                          {headline.length}/40
                        </span>
                      </label>
                      <input
                        type="text"
                        value={headline}
                        onChange={e => setHeadline(e.target.value)}
                        disabled={isPrefillingSource}
                        placeholder="Short, catchy headline"
                        maxLength={40}
                        className="input input-md w-full bg-system-gray-900/50 border-system-gray-700/50 text-white placeholder:text-system-gray-600 focus:border-brand focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-system-gray-400 mb-1.5 flex items-center justify-between">
                        <span>Description</span>
                        <span className="text-system-gray-600">
                          {description.length}/255
                        </span>
                      </label>
                      <input
                        type="text"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        disabled={isPrefillingSource}
                        placeholder="Additional context"
                        maxLength={255}
                        className="input input-md w-full bg-system-gray-900/50 border-system-gray-700/50 text-white placeholder:text-system-gray-600 focus:border-brand focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {/* Shared CTA (destination-specific values override this) */}
                  <div>
                    <label className="text-xs font-medium text-system-gray-400 mb-1.5 block">
                      Call to Action (shared default)
                    </label>
                    <select
                      value={callToAction}
                      onChange={e => setCallToAction(e.target.value)}
                      disabled={isPrefillingSource}
                      className="select select-md w-full bg-system-gray-900/50 border-system-gray-700/50 text-white focus:border-brand focus:outline-none [&>option]:bg-system-gray-800 [&>option]:text-white disabled:opacity-60 disabled:cursor-not-allowed p-0 pl-2"
                    >
                      {CTA_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Feed products — on save, every media asset joins each
                    selected product's feed pool (MANUAL AssetProductTags),
                    feeding the Meta supplemental feed. */}
                  <div>
                    <label className="text-xs font-medium text-system-gray-400 mb-1.5 block">
                      Feed products (assets join each product&apos;s pool)
                    </label>
                    <ProductMultiSelect
                      value={feedProducts}
                      onChange={setFeedProducts}
                      disabled={isPrefillingSource}
                    />
                  </div>

                  {/* Destination & Tracking. Profile-visit ad sets normally
                    auto-resolve to the IG profile URL, but some IG engagement
                    ad sets are still rejected by Meta with error 2061015
                    ("website URL required"). When the backend signals that via
                    requiredFields, we have to unlock this section so the user
                    can supply one — otherwise the warning above is unactionable. */}
                  {(() => {
                    const destUrlRequired =
                      requiredFields.includes("destinationUrl")
                    const sectionLocked = allProfileVisit && !destUrlRequired
                    return (
                      <div
                        className={`space-y-3 rounded-lg border border-system-gray-800/50 p-4 ${
                          sectionLocked ? "opacity-40 pointer-events-none" : ""
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-medium text-system-gray-400">
                            {destUrlRequired
                              ? "Destination URL (required by Meta)"
                              : allProfileVisit
                                ? "Destination URL (auto-set to IG profile)"
                                : "Destination URL (shared default)"}
                          </label>
                          {allProfileVisit && !destUrlRequired && (
                            <span className="text-[9px] text-system-gray-500 bg-system-gray-800/50 px-2 py-0.5 rounded">
                              Auto
                            </span>
                          )}
                          {destUrlRequired && (
                            <span className="text-[9px] text-system-warning-400 bg-system-warning-500/15 px-2 py-0.5 rounded">
                              Required
                            </span>
                          )}
                        </div>
                        <input
                          type="url"
                          value={destinationUrl}
                          disabled={isPrefillingSource}
                          onChange={e => setDestinationUrl(e.target.value)}
                          onBlur={e => {
                            // On blur (and on paste — see below), normalize the URL
                            // and split any query string into the tracking section.
                            // We deliberately do NOT do this on every keystroke
                            // because `new URL(...).pathname` always returns "/" for
                            // host-only inputs, which corrupts what the user typed.
                            const raw = normalizeUrl(e.target.value)
                            if (!raw) return
                            try {
                              const url = new URL(raw)
                              if (!url.hostname.includes(".")) {
                                setDestinationUrl(raw)
                                return
                              }
                              const parsed = Array.from(
                                url.searchParams.entries(),
                              ).map(([key, value]) => ({ key, value }))
                              // Preserve the user's pathname exactly — only strip the
                              // synthetic "/" the URL spec adds when the user typed
                              // host-only (e.g. "https://www.tr.com").
                              const trailingSlashAdded =
                                url.pathname === "/" &&
                                !/\/(\?|$)/.test(e.target.value)
                              const cleanPath = trailingSlashAdded
                                ? ""
                                : url.pathname
                              setDestinationUrl(`${url.origin}${cleanPath}`)
                              if (parsed.length > 0) {
                                setTracking(parsed)
                              }
                            } catch {
                              setDestinationUrl(raw)
                            }
                          }}
                          onPaste={e => {
                            // Pasted text usually contains the full URL with params —
                            // run the same normalization synchronously so the user
                            // sees the cleaned URL + populated tracking immediately.
                            const pasted = e.clipboardData.getData("text")
                            if (!pasted) return
                            e.preventDefault()
                            const raw = normalizeUrl(pasted)
                            try {
                              const url = new URL(raw)
                              if (!url.hostname.includes(".")) {
                                setDestinationUrl(raw)
                                return
                              }
                              const parsed = Array.from(
                                url.searchParams.entries(),
                              ).map(([key, value]) => ({ key, value }))
                              const trailingSlashAdded =
                                url.pathname === "/" && !/\/(\?|$)/.test(pasted)
                              const cleanPath = trailingSlashAdded
                                ? ""
                                : url.pathname
                              setDestinationUrl(`${url.origin}${cleanPath}`)
                              if (parsed.length > 0) {
                                setTracking(parsed)
                              }
                            } catch {
                              setDestinationUrl(raw)
                            }
                          }}
                          placeholder="https://... (paste URL with params to auto-fill)"
                          className="input input-md w-full bg-system-gray-900/50 border-system-gray-700/50 text-white placeholder:text-system-gray-600 focus:border-brand focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                        />
                        <p className="text-[10px] text-system-gray-500">
                          Parameters below are sent via Meta&apos;s{" "}
                          <span className="font-mono">url_tags</span> field.
                          Per-destination URL overrides can be set in the
                          destinations bar above.
                        </p>

                        {/* Single-string UTM entry — paste or type the whole
                          query string at once; it stays two-way synced with
                          the parameter rows below. */}
                        <div className="space-y-1">
                          <label className="text-[10px] font-medium text-system-gray-500">
                            UTM string (paste all params at once)
                          </label>
                          <input
                            type="text"
                            value={utmStringDraft ?? trackingToString(tracking)}
                            disabled={isPrefillingSource}
                            onFocus={() =>
                              setUtmStringDraft(trackingToString(tracking))
                            }
                            onChange={e => {
                              setUtmStringDraft(e.target.value)
                              setTracking(parseTrackingString(e.target.value))
                            }}
                            onPaste={e => {
                              // Merge the pasted params into the existing ones
                              // (pasted values win on key clash) instead of
                              // letting the browser splice raw text into the
                              // string at the cursor.
                              const pasted = e.clipboardData.getData("text")
                              const parsed = parseTrackingString(pasted)
                              if (parsed.length === 0) return
                              e.preventDefault()
                              setTracking(prev => {
                                const merged = prev.filter(
                                  p => p.key && p.key !== "__custom__",
                                )
                                for (const p of parsed) {
                                  const at = merged.findIndex(
                                    m => m.key === p.key,
                                  )
                                  if (at === -1) merged.push(p)
                                  else merged[at] = p
                                }
                                return merged
                              })
                              setUtmStringDraft(null)
                            }}
                            onBlur={() => setUtmStringDraft(null)}
                            placeholder="utm_source=facebook&utm_medium=cpc&utm_campaign=..."
                            className="input input-md w-full bg-system-gray-900/50 border-system-gray-700/50 text-white placeholder:text-system-gray-600 focus:border-brand focus:outline-none font-mono text-xs disabled:opacity-60 disabled:cursor-not-allowed"
                          />
                        </div>

                        {/* URL Parameters */}
                        <div className="space-y-2">
                          {tracking.map((param, i) => {
                            const knownKeys = [
                              "utm_source",
                              "utm_medium",
                              "utm_campaign",
                              "utm_term",
                              "utm_content",
                              "fbclid",
                              "gclid",
                              "ref",
                              "source",
                              "campaign_id",
                              "ad_id",
                              "placement",
                            ]
                            const isCustomSentinel = param.key === "__custom__"
                            const isUnknownCustomKey =
                              !!param.key &&
                              !isCustomSentinel &&
                              !knownKeys.includes(param.key)
                            const updateKey = (value: string) =>
                              setTracking(prev =>
                                prev.map((p, j) =>
                                  j === i ? { ...p, key: value } : p,
                                ),
                              )
                            return (
                              <div
                                key={`param-${i}`}
                                className="flex items-center gap-2"
                              >
                                {isCustomSentinel && (
                                  <input
                                    type="text"
                                    value=""
                                    onChange={e => updateKey(e.target.value)}
                                    autoFocus
                                    placeholder="custom_param"
                                    className="input input-md w-36 flex-shrink-0 bg-system-gray-900/50 border border-system-gray-700/50 rounded text-white placeholder:text-system-gray-600 focus:border-brand focus:outline-none font-mono text-xs px-2"
                                  />
                                )}
                                {isUnknownCustomKey && (
                                  <input
                                    type="text"
                                    value={param.key}
                                    onChange={e => updateKey(e.target.value)}
                                    placeholder="param_name"
                                    className="input input-md w-36 flex-shrink-0 bg-system-gray-900/50 border border-system-gray-700/50 rounded text-white placeholder:text-system-gray-600 focus:border-brand focus:outline-none font-mono text-xs px-2"
                                  />
                                )}
                                {!isCustomSentinel && !isUnknownCustomKey && (
                                  <select
                                    value={param.key}
                                    onChange={e =>
                                      setTracking(prev =>
                                        prev.map((p, j) =>
                                          j === i
                                            ? { ...p, key: e.target.value }
                                            : p,
                                        ),
                                      )
                                    }
                                    className="select select-md w-36 flex-shrink-0 bg-system-gray-900/50 border-system-gray-700/50 text-white focus:border-brand focus:outline-none font-mono text-xs [&>option]:bg-system-gray-800 [&>option]:text-white p-0 pl-2"
                                  >
                                    {(() => {
                                      const used = new Set(
                                        tracking
                                          .filter((_, j) => j !== i)
                                          .map(p => p.key),
                                      )
                                      const groups = [
                                        {
                                          label: "UTM",
                                          keys: [
                                            "utm_source",
                                            "utm_medium",
                                            "utm_campaign",
                                            "utm_term",
                                            "utm_content",
                                          ],
                                        },
                                        {
                                          label: "Tracking",
                                          keys: [
                                            "fbclid",
                                            "gclid",
                                            "ref",
                                            "source",
                                          ],
                                        },
                                        {
                                          label: "Meta",
                                          keys: [
                                            "campaign_id",
                                            "ad_id",
                                            "placement",
                                          ],
                                        },
                                      ]
                                      return (
                                        <>
                                          <option value="" disabled>
                                            Select param...
                                          </option>
                                          {groups.map(g => {
                                            const available = g.keys.filter(
                                              k => !used.has(k),
                                            )
                                            if (available.length === 0) {
                                              return null
                                            }
                                            return (
                                              <optgroup
                                                key={g.label}
                                                label={g.label}
                                              >
                                                {available.map(k => (
                                                  <option key={k} value={k}>
                                                    {k}
                                                  </option>
                                                ))}
                                              </optgroup>
                                            )
                                          })}
                                          <option value="__custom__">
                                            Custom...
                                          </option>
                                        </>
                                      )
                                    })()}
                                  </select>
                                )}
                                <span className="text-system-gray-600 text-[10px]">
                                  =
                                </span>
                                <input
                                  type="text"
                                  value={param.value}
                                  onChange={e =>
                                    setTracking(prev =>
                                      prev.map((p, j) =>
                                        j === i
                                          ? { ...p, value: e.target.value }
                                          : p,
                                      ),
                                    )
                                  }
                                  placeholder="value"
                                  className="input input-md flex-1 bg-system-gray-900/50 border-system-gray-700/50 text-white placeholder:text-system-gray-600 focus:border-brand focus:outline-none"
                                />
                                <button
                                  onClick={() =>
                                    setTracking(prev =>
                                      prev.filter((_, j) => j !== i),
                                    )
                                  }
                                  className="text-system-gray-500 hover:text-system-error-400 transition flex-shrink-0"
                                >
                                  <XMarkIcon className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )
                          })}
                          <button
                            onClick={() =>
                              setTracking(prev => [
                                ...prev,
                                { key: "", value: "" },
                              ])
                            }
                            className="text-[10px] text-brand hover:text-brand/80 font-medium transition"
                          >
                            + Add parameter
                          </button>
                        </div>
                      </div>
                    )
                  })()}
                </section>

                {/* Source-mode CTA: send the editor back to AdBlaster (which
                  owns the BlastTargetPickerModal + actual launch). Fresh-build
                  mode keeps the save-draft button below. The previous
                  in-page blast call from AdForge — and its dependence on
                  `selectedAdSetIds` — is gone now that AdBlaster's blast
                  modal is the single launch surface. */}
                {sourceAd ? (
                  <button
                    type="button"
                    onClick={() => navigate(brandRoutes.AD_BLASTER.CREATIVES)}
                    disabled={isPrefillingSource}
                    className="btn btn-primary w-full gap-2 font-bold disabled:opacity-50"
                  >
                    {isPrefillingSource ? (
                      <span className="loading loading-spinner loading-sm" />
                    ) : (
                      <RocketLaunchIcon className="w-5 h-5" />
                    )}
                    {isPrefillingSource
                      ? isSourcePost
                        ? "Loading source post…"
                        : "Loading source ad…"
                      : "Done — pick destinations in AdBlaster"}
                  </button>
                ) : (
                  // Fresh-build mode is save-only — destinations get picked from
                  // AdBlaster's blast modal, not here.
                  <button
                    type="button"
                    onClick={() => void handleSaveDraft()}
                    disabled={
                      isSavingDraft ||
                      selectedAssets.length === 0 ||
                      !primaryText.trim() ||
                      !headline.trim() ||
                      !!tagRequirement
                    }
                    title={
                      tagRequirement
                        ? "Add the required tags before saving"
                        : undefined
                    }
                    className="btn btn-primary w-full gap-2 font-bold disabled:opacity-50"
                  >
                    {isSavingDraft ? (
                      <span className="loading loading-spinner loading-sm" />
                    ) : (
                      <RocketLaunchIcon className="w-5 h-5" />
                    )}
                    {isSavingDraft ? "Saving draft…" : "Save as draft"}
                  </button>
                )}
              </div>

              {/* RIGHT: Placements showcase — hidden in source mode (the
                user is launching an organic post or existing ad as-is, so
                per-placement preview/edit isn't applicable). One tile per
                Meta aspect-ratio slot (Feed 4:5, Stories 9:16, Square 1:1;
                4:5 covers FB Feed and the IG feeds alike). Per-placement
                uploads/crops override the
                base asset; slots left empty show the base asset (Meta
                auto-pads at launch). For carousel, a chip selector picks
                which card to preview.

                Conditional render rather than `hidden`: Tailwind's `flex`
                utility ({display:flex}) overrides the HTML `hidden`
                attribute, so the showcase was still showing up on mobile
                (where `lg:col-span-2` doesn't constrain it).

                Source-mode visibility: hidden by default (blast post/ad
                as-is), but shown when the user is in edit-creative mode —
                either because they clicked "Edit creative (optional)" or
                because a non-PV target auto-expanded the section. Tied to
                the same `showCreativeFields` gate as the override fields,
                so URL / CTA / UTM / placements all surface together. */}
              {(!sourceAd || showCreativeFields) && (
                <div className="lg:col-span-2 flex flex-col items-stretch">
                  <div className="sticky top-8 w-full space-y-3">
                    {(() => {
                      if (adAssets.length === 0) {
                        return (
                          <div className="aspect-square w-full rounded-lg border border-dashed border-system-gray-700/50 flex items-center justify-center text-system-gray-500 text-xs">
                            Drop assets to see placements
                          </div>
                        )
                      }
                      const SLOTS = [
                        {
                          // One 4:5 image serves every feed surface — FB Feed /
                          // Marketplace / Search and IG Stream / Explore /
                          // Profile Feed. The old 1.91:1 `landscape` slot is
                          // gone; Meta renders 4:5 in FB Feed and it's the
                          // taller, better-performing frame.
                          slot: "portrait" as const,
                          label: "Feed",
                          ratio: "4:5",
                          aspectClass: "aspect-[4/5]",
                        },
                        {
                          slot: "stories" as const,
                          label: "Stories / Reels",
                          ratio: "9:16",
                          aspectClass: "aspect-[9/16]",
                        },
                        {
                          slot: "square" as const,
                          label: "Square",
                          ratio: "1:1",
                          aspectClass: "aspect-square",
                        },
                      ]
                      const safeIndex = Math.min(
                        selectedCarouselIndex,
                        adAssets.length - 1,
                      )
                      const card =
                        template === "carousel"
                          ? adAssets[safeIndex]
                          : adAssets[0]
                      const variants = placementAssetsByAsset[card.id] || {}
                      // True while the bulk prefill lookup is still resolving
                      // this card's saved placement variants — drives the
                      // per-tile loading spinner below.
                      const isLoadingPlacements =
                        !!card.assetId && prefillingAssetIds.has(card.assetId)
                      const customCount = SLOTS.filter(
                        s => variants[s.slot],
                      ).length
                      const fixAllThisCard = () =>
                        runBulkImageJob([card], { enhance: false })
                      // Carousel-only: fix EVERY card's placements in one shot.
                      // Hands the whole adAssets list to the background job so the
                      // user doesn't have to click through each card.
                      const fixAllCarouselCards = () =>
                        runBulkImageJob(adAssets, { enhance: false })
                      // How many slots are still empty across the whole carousel —
                      // used to disable the button when there's nothing to do.
                      const carouselUnfilledCount =
                        template === "carousel"
                          ? adAssets.reduce((acc, c) => {
                              const v = placementAssetsByAsset[c.id] || {}
                              return acc + SLOTS.filter(s => !v[s.slot]).length
                            }, 0)
                          : 0
                      const fixOnePlacement = async (
                        slot: (typeof SLOTS)[number]["slot"],
                        customPrompt?: string,
                      ) => {
                        // AVIF can't go through the image-edit pipeline — same
                        // exclusion as runBulkImageJob, applied here because this
                        // path calls extendAllPlacements directly.
                        if (isAvifImage(card)) {
                          toast(
                            "AVIF images can't be enhanced — re-upload as JPEG or PNG.",
                          )
                          return
                        }
                        // skipExisting=false so the button regenerates the slot
                        // even when it already has a variant — gives the user a
                        // way to re-roll a bad AI output.
                        const key = `${card.id}:${slot}`
                        setExtendingPlacementKeys(prev => {
                          const nextKeys = new Set(prev)
                          nextKeys.add(key)
                          return nextKeys
                        })
                        try {
                          const next = await extendAllPlacements(
                            [card],
                            placementAssetsByAsset,
                            {
                              slots: [slot],
                              skipExisting: false,
                              customPrompt,
                            },
                          )
                          setPlacementAssetsByAsset(next)
                        } finally {
                          setExtendingPlacementKeys(prev => {
                            const nextKeys = new Set(prev)
                            nextKeys.delete(key)
                            return nextKeys
                          })
                        }
                      }
                      const clearOnePlacement = (
                        slot: (typeof SLOTS)[number]["slot"],
                      ) => {
                        setPlacementAssetsByAsset(prev => {
                          const existing = prev[card.id]
                          if (!existing || !existing[slot]) return prev
                          const nextForCard = { ...existing }
                          delete nextForCard[slot]
                          return { ...prev, [card.id]: nextForCard }
                        })
                      }
                      const openPlacementUpload = (
                        slot: (typeof SLOTS)[number]["slot"],
                      ) => {
                        setUploadForPlacement({ cardId: card.id, slot })
                        placementUploadRef.current?.click()
                      }
                      return (
                        <>
                          {/* Header: title + customized count on top, Fix-all
                          and Customize buttons on a dedicated action row so
                          they don't jam against the title. */}
                          <div className="rounded-lg border border-brand/15 bg-brand/5 px-3 py-2.5 space-y-2">
                            <div className="flex items-baseline justify-between gap-2">
                              <p className="text-sm font-semibold text-white truncate">
                                {template === "carousel"
                                  ? `Card ${safeIndex + 1}`
                                  : "Placements"}
                              </p>
                              <p className="text-[10px] text-system-gray-500 flex-shrink-0">
                                {customCount}/{SLOTS.length} custom
                              </p>
                            </div>
                            {isExtendingPlacements && extendProgress && (
                              <p className="text-[10px] text-brand">
                                Extending {extendProgress.done}/
                                {extendProgress.total} — {extendProgress.label}
                              </p>
                            )}
                            <button
                              disabled={isExtendingPlacements || !!jobsRunning}
                              onClick={fixAllThisCard}
                              className="btn btn-xs bg-brand/15 border border-brand/30 text-brand hover:bg-brand/25 gap-1 w-full disabled:opacity-50"
                            >
                              {jobsRunning ? (
                                <span className="loading loading-spinner loading-xs" />
                              ) : (
                                <SparklesIcon className="w-3 h-3" />
                              )}
                              {(() => {
                                if (jobsRunning) return "Working…"
                                return template === "carousel"
                                  ? "Fix this card with AI"
                                  : "Fix all with AI"
                              })()}
                            </button>
                            {/* Carousel-only: one click runs the AI-extend flow
                            across every card × every placement ratio. Skips
                            slots already populated (see extendAll's
                            skipExisting default). */}
                            {template === "carousel" && adAssets.length > 1 && (
                              <button
                                disabled={
                                  isExtendingPlacements ||
                                  !!jobsRunning ||
                                  carouselUnfilledCount === 0
                                }
                                onClick={fixAllCarouselCards}
                                className="btn btn-xs bg-brand text-white hover:bg-brand/90 gap-1 w-full disabled:opacity-50"
                                title={
                                  carouselUnfilledCount === 0
                                    ? "Every card already has every placement"
                                    : `Generate ${carouselUnfilledCount} missing placement variant${carouselUnfilledCount === 1 ? "" : "s"} across ${adAssets.length} cards`
                                }
                              >
                                {jobsRunning ? (
                                  <span className="loading loading-spinner loading-xs" />
                                ) : (
                                  <SparklesIcon className="w-3 h-3" />
                                )}
                                {jobsRunning
                                  ? "Working…"
                                  : `Fix all ${adAssets.length} cards`}
                                {!jobsRunning && carouselUnfilledCount > 0 && (
                                  <span className="text-[10px] opacity-70">
                                    ({carouselUnfilledCount} left)
                                  </span>
                                )}
                              </button>
                            )}
                          </div>
                          {template === "carousel" && adAssets.length > 1 && (
                            <p className="text-[10px] text-system-gray-500 italic">
                              Click another card in the carousel to switch.
                            </p>
                          )}
                          {/* AnimatePresence + a key on the motion list lets the
                          tiles cross-fade when the user switches carousel
                          cards or starts/clears a search. `mode="wait"` so
                          the outgoing list finishes its exit before the new
                          one cascades in. */}
                          <AnimatePresence mode="wait">
                            <motion.div
                              key={card.id}
                              variants={placementsListVariants}
                              initial="hidden"
                              animate="visible"
                              exit="exit"
                              className="flex flex-col gap-3"
                            >
                              {SLOTS.map(cfg => {
                                const variant = variants[cfg.slot]
                                const img = variant ?? card
                                const isCustom = !!variant
                                // This exact tile is mid-generation from the
                                // custom-prompt editor (or its AI-fix button).
                                const isExtendingThis =
                                  extendingPlacementKeys.has(
                                    `${card.id}:${cfg.slot}`,
                                  )
                                return (
                                  <motion.div
                                    key={cfg.slot}
                                    variants={placementTileVariants}
                                    className="flex flex-col gap-1.5"
                                  >
                                    <PlacementDroppable
                                      cardId={card.id}
                                      slot={cfg.slot}
                                      className={cn(
                                        "relative bg-system-gray-900 rounded-lg overflow-hidden ring-1 ring-system-gray-800 transition",
                                        cfg.aspectClass,
                                        isCustom && "ring-brand/40",
                                      )}
                                    >
                                      {({ isOver }) => (
                                        <>
                                          {img?.url ? (
                                            // Custom variant fills the slot (cover);
                                            // base asset is shown contained so empty
                                            // bars on top/sides match what Meta will
                                            // auto-pad at launch. Video assets show
                                            // the poster/first frame only — preview
                                            // playback happens in the dedicated
                                            // VideoPreviewModal off the card icon.
                                            img.type === "video" ? (
                                              <video
                                                src={img.url}
                                                poster={
                                                  img.thumbnailUrl || undefined
                                                }
                                                muted
                                                controls
                                                playsInline
                                                preload="metadata"
                                                className={cn(
                                                  "w-full h-full",
                                                  isCustom
                                                    ? "object-cover"
                                                    : "object-contain",
                                                )}
                                              />
                                            ) : (
                                              <img
                                                src={
                                                  img.url || img.thumbnailUrl
                                                }
                                                alt={cfg.label}
                                                className={cn(
                                                  "w-full h-full",
                                                  isCustom
                                                    ? "object-cover"
                                                    : "object-contain",
                                                )}
                                                loading="lazy"
                                                decoding="async"
                                              />
                                            )
                                          ) : (
                                            <div className="w-full h-full flex items-center justify-center text-system-gray-500 text-[10px]">
                                              No asset
                                            </div>
                                          )}
                                          {/* Assigned layout. A per-placement
                                          arrangement wins outright; else
                                          custom variants re-solve the flow
                                          at the tile's ratio, and letterboxed
                                          base media letterboxes the overlay
                                          with it (it bakes into the media,
                                          never onto the padding bars). */}
                                          {card.overlayDoc && img?.url && (
                                            <CardOverlayPreview
                                              doc={
                                                card.overlayDocByPlacement?.[
                                                  cfg.slot
                                                ] ??
                                                (isCustom
                                                  ? rescaleDoc(
                                                      card.overlayDoc,
                                                      dimsForRatioLabel(
                                                        cfg.ratio,
                                                      ),
                                                    )
                                                  : card.overlayDoc)
                                              }
                                              fit={
                                                isCustom ||
                                                card.overlayDocByPlacement?.[
                                                  cfg.slot
                                                ]
                                                  ? "cover"
                                                  : "contain"
                                              }
                                            />
                                          )}
                                          {isCustom && (
                                            <span className="absolute top-1.5 left-1.5 bg-brand text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                                              AI
                                            </span>
                                          )}
                                          {/* Loading spinner while this card's
                                          saved placement variants are being
                                          fetched. Only on empty (non-custom)
                                          tiles — those are the ones a prefill
                                          result could still fill. */}
                                          {isLoadingPlacements &&
                                            !isCustom &&
                                            !isExtendingThis && (
                                              <div
                                                aria-live="polite"
                                                className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm pointer-events-none"
                                              >
                                                <span className="loading loading-spinner loading-sm text-brand" />
                                              </div>
                                            )}
                                          {/* Generating overlay — this exact
                                          tile is being (re)generated from the
                                          custom-prompt editor. Shown over both
                                          empty and already-custom tiles (a
                                          re-roll regenerates an existing one). */}
                                          {isExtendingThis && (
                                            <div
                                              aria-live="polite"
                                              className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/60 backdrop-blur-sm pointer-events-none"
                                            >
                                              <span className="loading loading-spinner loading-md text-brand" />
                                              <span className="text-[9px] uppercase tracking-wide text-white font-semibold">
                                                Generating…
                                              </span>
                                            </div>
                                          )}
                                          {/* Drop-target overlay — appears while a
                                        drawer pool item is hovering over this
                                        tile so the user sees exactly which
                                        slot they're about to set. */}
                                          {isOver && (
                                            <div className="absolute inset-0 bg-brand/20 ring-2 ring-brand pointer-events-none flex items-center justify-center">
                                              <span className="text-[10px] font-bold text-white bg-brand/90 px-2 py-0.5 rounded">
                                                Drop to set
                                              </span>
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </PlacementDroppable>
                                    {/* Label + action row sits OUTSIDE the image so
                                  it never obstructs the artwork. Label and
                                  ratio on the left, four compact icon-action
                                  buttons (Upload, Library, AI Fix, Remove)
                                  on the right — all the same operations the
                                  legacy customize-by-placement modal had. */}
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="min-w-0">
                                        <div className="text-[11px] font-semibold text-white truncate">
                                          {cfg.label}
                                        </div>
                                        <div className="text-[10px] text-system-gray-500">
                                          {cfg.ratio}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-0.5 flex-shrink-0">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setPlacementPreviewTarget({
                                              card,
                                              slot: cfg.slot,
                                            })
                                          }
                                          title={`Preview ${cfg.label} full page`}
                                          aria-label={`Preview ${cfg.label} full page`}
                                          className="p-1 rounded-md text-system-gray-400 hover:text-brand hover:bg-system-gray-800 transition"
                                        >
                                          <ArrowsPointingOutIcon className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            openPlacementUpload(cfg.slot)
                                          }
                                          title={`Upload an image for ${cfg.ratio}`}
                                          aria-label={`Upload for ${cfg.label}`}
                                          className="p-1 rounded-md text-system-gray-400 hover:text-brand hover:bg-system-gray-800 transition"
                                        >
                                          <ArrowUpTrayIcon className="w-3.5 h-3.5" />
                                        </button>
                                        {card.overlayDoc && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setPlacementOverlayTarget({
                                                card,
                                                slot: cfg.slot,
                                                slotLabel: cfg.label,
                                                ratio: cfg.ratio,
                                                media: img?.url
                                                  ? {
                                                      type:
                                                        img.type === "video"
                                                          ? "video"
                                                          : "image",
                                                      src: img.url,
                                                    }
                                                  : null,
                                              })
                                            }
                                            title={`Arrange the layout for ${cfg.label} (${cfg.ratio})`}
                                            aria-label={`Arrange layout for ${cfg.label}`}
                                            className={cn(
                                              "p-1 rounded-md transition hover:bg-system-gray-800",
                                              card.overlayDocByPlacement?.[
                                                cfg.slot
                                              ]
                                                ? "text-brand"
                                                : "text-system-gray-400 hover:text-brand",
                                            )}
                                          >
                                            <PaintBrushIcon className="w-3.5 h-3.5" />
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          disabled={isExtendingPlacements}
                                          onClick={() => {
                                            // Toggle: clicking again on the
                                            // same tile closes the editor.
                                            setExtendPromptEditor(prev =>
                                              prev &&
                                              prev.cardId === card.id &&
                                              prev.slot === cfg.slot
                                                ? null
                                                : {
                                                    cardId: card.id,
                                                    slot: cfg.slot,
                                                    draft: buildExtendPrompt(
                                                      cfg.ratio,
                                                    ),
                                                  },
                                            )
                                          }}
                                          title={
                                            isCustom
                                              ? `Regenerate ${cfg.ratio} with AI — edit prompt before generating`
                                              : `Extend to ${cfg.ratio} with AI — edit prompt before generating`
                                          }
                                          aria-label={`AI extend with custom prompt for ${cfg.label}`}
                                          className="p-1 rounded-md text-brand hover:bg-brand/15 transition disabled:opacity-50"
                                        >
                                          <PencilSquareIcon className="w-3.5 h-3.5" />
                                        </button>
                                        {isCustom && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              clearOnePlacement(cfg.slot)
                                            }
                                            title="Remove custom variant — falls back to base"
                                            aria-label={`Remove custom variant for ${cfg.label}`}
                                            className="p-1 rounded-md text-system-gray-400 hover:text-red-500 hover:bg-system-gray-800 transition"
                                          >
                                            <XMarkIcon className="w-3.5 h-3.5" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    {/* Inline custom-prompt editor — opens
                                      under the action row when the pencil
                                      icon is clicked for this exact tile.
                                      Pre-filled with the default uncrop
                                      prompt so users can tweak instead of
                                      starting from a blank box. */}
                                    {extendPromptEditor &&
                                      extendPromptEditor.cardId === card.id &&
                                      extendPromptEditor.slot === cfg.slot && (
                                        <div className="rounded-md border border-brand/30 bg-system-gray-900/60 p-2 space-y-1.5">
                                          <span className="text-[9px] uppercase tracking-wide text-system-gray-500">
                                            Custom prompt for {cfg.ratio}
                                          </span>
                                          <textarea
                                            value={extendPromptEditor.draft}
                                            onChange={e =>
                                              setExtendPromptEditor(prev =>
                                                prev
                                                  ? {
                                                      ...prev,
                                                      draft: e.target.value,
                                                    }
                                                  : prev,
                                              )
                                            }
                                            disabled={isExtendingPlacements}
                                            rows={4}
                                            className="textarea textarea-xs w-full bg-system-gray-800 border-system-gray-700 text-white text-[11px] focus:border-brand focus:outline-none disabled:opacity-50"
                                            placeholder="Describe how to fill the new frame…"
                                          />
                                          <div className="flex items-center justify-end gap-1.5">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                setExtendPromptEditor(null)
                                              }
                                              disabled={isExtendingPlacements}
                                              className="btn btn-xs btn-ghost text-system-gray-400 disabled:opacity-50"
                                            >
                                              Cancel
                                            </button>
                                            <button
                                              type="button"
                                              onClick={async () => {
                                                const draft =
                                                  extendPromptEditor.draft
                                                setExtendPromptEditor(null)
                                                await fixOnePlacement(
                                                  cfg.slot,
                                                  draft,
                                                )
                                              }}
                                              disabled={
                                                isExtendingPlacements ||
                                                !extendPromptEditor.draft.trim()
                                              }
                                              className="btn btn-xs bg-brand text-white hover:bg-brand/90 gap-1 disabled:opacity-50"
                                            >
                                              <SparklesIcon className="w-3 h-3" />
                                              Generate
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                  </motion.div>
                                )
                              })}
                            </motion.div>
                          </AnimatePresence>
                        </>
                      )
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Hidden file input shared by every placement-upload trigger. The
              currently targeted slot is held in `uploadForPlacement`. */}
            <input
              ref={placementUploadRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async e => {
                const file = e.target.files?.[0]
                e.target.value = ""
                if (!file || !uploadForPlacement) {
                  setUploadForPlacement(null)
                  return
                }
                const target = uploadForPlacement
                setUploadForPlacement(null)
                const [img] = await uploadFiles([file])
                if (img) {
                  setPlacementAssetsByAsset(prev => ({
                    ...prev,
                    [target.cardId]: {
                      ...(prev[target.cardId] || {}),
                      [target.slot]: img,
                    },
                  }))
                }
              }}
            />

            {/* Picked-asset pool is now sourced entirely from the
              SelectedAssetsDrawer (search/upload happens there). The
              standalone asset-picker modal is gone. */}

            {/* Create with AI panel renders permanently below the card grid —
              see the `<AiActionDialog />` mounted after `<AdCardSlots />`. */}
          </div>
        </div>

        {/* Selected Assets panel — staged pool, now a permanent right column.
            Drag items into a card slot on the left, or drag a slot's asset
            back here to clear it. */}
        <SelectedAssetsDrawer
          pickedAssets={pickedAssets}
          adAssetIds={adAssets.map(a => a.id)}
          placementLabelsByAssetId={placementLabelsByAssetId}
          isDemoEnabled={isDemoEnabled}
          isUploading={isUploading}
          onRemoveFromPool={id =>
            dispatch(adForgeActions.removePickedAsset(id))
          }
          onAddToPool={asset => dispatch(adForgeActions.addPickedAsset(asset))}
          onUploadClick={() => mainUploadInputRef.current?.click()}
          onScrapeClick={() => setShowScrapeDialog(true)}
          onPickThumbnail={asset => void handlePickThumbnail(asset)}
        />
      </div>

      <ScrapeFromUrlDialog
        isOpen={showScrapeDialog}
        onClose={() => setShowScrapeDialog(false)}
        onScraped={images => {
          for (const img of images) {
            dispatch(adForgeActions.addSelectedAsset(img))
          }
        }}
      />
      <AdNameBuilder
        initialSelection={adTags}
        isOpen={showAdNameBuilder}
        onClose={() => setShowAdNameBuilder(false)}
        onApply={(name, tags) => {
          setAdName(name)
          setAdTags(tags)
        }}
      />
      <UnsavedChangesDialog
        isOpen={blocker.state === "blocked"}
        isSaving={isSavingDraft}
        canSave={
          selectedAssets.length > 0 && !!primaryText.trim() && !!headline.trim()
        }
        onSave={() => {
          // Save without the built-in redirect, then continue to wherever the
          // user was navigating (the held destination).
          void handleSaveDraft({ navigateAfter: false }).then(ok => {
            if (ok) blocker.proceed()
          })
        }}
        onLeave={blocker.proceed}
        onStay={blocker.reset}
      />
    </DndContext>
  )
}
